import { Suspense, lazy, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode, type WheelEvent as ReactWheelEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Dialog } from "@radix-ui/themes";
import type { CellClickedEvent, ColDef, ColGroupDef, FilterChangedEvent, GridApi, GridReadyEvent, ICellRendererParams, ModelUpdatedEvent, ValueFormatterParams } from "ag-grid-community";
import { colorSchemeDark, themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import { createProject, fetchProjects, type Project } from "../../api/projects";
import { acceptInvitation } from "../../api/members";
import { createPlot, type PlotScope } from "../../api/plots";
import { ensureTsangpoThreadProject, fetchTsangpoBuildJobs, fetchTsangpoProjectSpec, planTsangpoCampaign, TSANGPO_FALLBACK_SPEC, type TsangpoBuildJob, type TsangpoCampaignSpec, type TsangpoProjectSpec } from "../../api/tsangpo";
import type { CodeRef } from "../../api/code";
import { MembersPanel } from "../../components/members/MembersPanel";
import { ThreadLogo } from "../../components/ThreadLogo";
import type { CorrelogramColumn } from "../../components/data-explorer/Correlogram";
import { rankColumnsByCorrelationTo } from "../../components/data-explorer/correlationRanking";
import { CodeSourcePanel } from "../../components/code/CodeSourcePanel";
import type { PlotBuilderConfig } from "../../components/plot-builder/types";
import { PlotDataProvider, usePlotData } from "../../context/PlotDataContext";
import { ProvenanceProvider } from "../../context/ProvenanceContext";
import {
  classifyCompatibility,
  type DerivedThreadSelection,
  deriveThreadSelection,
  type CompatibilityClass,
  type ThreadAsset,
  type ThreadColumn,
  type ThreadRow,
  type ThreadSelectionState,
} from "../thread-design-system";
import { useV3Data } from "./useV3Data";
import { deriveProvenanceSelection, walkProvenanceDownstream, walkProvenanceUpstream } from "./provenance";
import { derivePivotCandidates, pivotIsAvailable } from "./pivots";
import type { ProjectionMode, V3PivotView, V3ProjectionDirection, V3SurfaceMode, V3WorkbenchData } from "./types";
import { parseV3VariantId, V3_VARIANTS, v3VariantById, type V3Variant, type V3VariantId } from "./variants";
import {
  DEFAULT_V3_DAG_LAYOUT,
  V3DeckDagLayer,
  type V3DagLayoutState,
  type V3DagPinnedPosition,
  type V3DeckViewState,
  v3DagProjectedClusterPosition,
  v3DagProjectedNodePosition,
} from "./V3DeckDagLayer";
import "../thread-design-system/thread-design-tokens.css";
import "./thread-v3.css";

const DataExplorerCorrelogram = lazy(() =>
  import("../../components/data-explorer/Correlogram").then((module) => ({ default: module.Correlogram })),
);
const SavedPlotsPanel = lazy(() => import("../../components/panels/SavedPlotsPanel"));
const PlotBuilderPanel = lazy(() => import("../../components/panels/PlotBuilderPanel"));

const MAX_ACTIVE_PARTICLES = 120;
const MELT_VISIBLE_CELL_LIMIT = 96;
const MELT_FRAGMENT_BUDGET = 96;
const SELECTION_FIT_NODE_LIMIT = 24;
const CELL_OVERLAY_MELT_MS = 1_640;
const CELL_OVERLAY_FREEZE_MS = 1_640;
const FILTERED_DAG_ROW_LIMIT = 2_500;
const FILTERED_DAG_WALK_LIMIT = 8_000;
const V3_CORRELOGRAM_GROUP_COLORS = ["#5ec8e0", "#67e8a5", "#a78bfa", "#fbbf24", "#f472b6", "#60a5fa", "#fb923c", "#2dd4bf"];
const V3_CORRELOGRAM_MAX_COLUMNS = 12;
const V3_CORRELOGRAM_RESPONSE_PRIORITY = [
  "cl",
  "liftcoefficient",
  "cm",
  "cmpitch",
  "cd",
  "dragcoefficient",
  "cy",
  "cfz",
  "ld",
  "liftodrag",
];
const UUID_LIKE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function displayValue(value: unknown): string {
  if (typeof value === "number") {
    if (Math.abs(value) >= 10000) return value.toExponential(2);
    if (Math.abs(value) < 0.001 && value !== 0) return value.toExponential(2);
    return Number.isInteger(value) ? String(value) : value.toFixed(4).replace(/0+$/, "").replace(/\.$/, "");
  }
  if (value == null || value === "") return "—";
  return String(value);
}

function compactCount(value: number): string {
  if (!Number.isFinite(value)) return "0";
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(value >= 10_000_000 ? 1 : 2)}M`;
  if (Math.abs(value) >= 10_000) return `${Math.round(value / 1_000)}K`;
  if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return value.toLocaleString();
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function wheelZoomViewState(event: ReactWheelEvent, deckLayer: HTMLElement, viewState: V3DeckViewState): V3DeckViewState {
  const rect = deckLayer.getBoundingClientRect();
  const centerX = rect.width / 2;
  const centerY = rect.height / 2;
  const pointerX = event.clientX - rect.left;
  const pointerY = event.clientY - rect.top;
  const deltaModeScale = event.deltaMode === 1 ? 18 : event.deltaMode === 2 ? 240 : 1;
  const currentScale = 2 ** viewState.zoom;
  const nextZoom = clampNumber(viewState.zoom - event.deltaY * deltaModeScale * 0.0022, -1.5, 6);
  const nextScale = 2 ** nextZoom;
  const worldUnderPointerX = viewState.target[0] + (pointerX - centerX) / currentScale;
  const worldUnderPointerY = viewState.target[1] + (pointerY - centerY) / currentScale;

  return {
    target: [
      worldUnderPointerX - (pointerX - centerX) / nextScale,
      worldUnderPointerY - (pointerY - centerY) / nextScale,
      viewState.target[2],
    ],
    zoom: nextZoom,
  };
}

function compatibilityClassName(kind: CompatibilityClass): string {
  return kind === "neutral" ? "" : `thread-compat-${kind}`;
}

function isProjectionNodeType(_type: string): boolean {
  return false;
}

function projectionCellForHandle(data: V3WorkbenchData, cellHandleId: string) {
  for (const cell of data.provenance.cellsByRowCol.values()) {
    if (cell.handleId === cellHandleId) return cell;
  }
  return null;
}

function sourceNodeIdsForCellHandle(data: V3WorkbenchData, cellHandleId: string): string[] {
  const cell = projectionCellForHandle(data, cellHandleId);
  return cell?.sourceNodeIds.length ? cell.sourceNodeIds : [cellHandleId];
}

function sourceNodeIdForCellHandle(data: V3WorkbenchData, cellHandleId: string): string {
  return sourceNodeIdsForCellHandle(data, cellHandleId)[0] ?? cellHandleId;
}

function isArtifactLikeNodeType(type: string): boolean {
  return [
    "plot",
    "plot_trace",
    "plot_point",
    "deck",
    "slide",
    "claim",
    "spreadsheet",
    "spreadsheet_range",
    "chat_thread",
    "email_thread",
    "smart_summary",
    "uvf_view",
    "notebook",
    "report",
    "decision_summary",
    "storyboard",
    "storyboard_shot",
    "artifact",
  ].includes(type);
}

function uniqueRealNodeIds(data: V3WorkbenchData, nodeIds: Iterable<string>): string[] {
  const unique: string[] = [];
  const seen = new Set<string>();
  for (const nodeId of nodeIds) {
    if (seen.has(nodeId)) continue;
    const node = data.provenance.nodesById.get(nodeId);
    if (!node || isProjectionNodeType(node.type)) continue;
    seen.add(nodeId);
    unique.push(nodeId);
  }
  return unique;
}

function sourceAtomIdsForCellHandle(data: V3WorkbenchData, cellNodeId: string, maxTargets: number): string[] {
  const primarySourceId = sourceNodeIdForCellHandle(data, cellNodeId);
  const upstream = [...walkProvenanceUpstream(data.provenance, [primarySourceId], 3, 28)];
  const downstreamContext = [...walkProvenanceDownstream(data.provenance, [primarySourceId], 2, 18)]
    .filter((nodeId) => {
      const node = data.provenance.nodesById.get(nodeId);
      return node && !isArtifactLikeNodeType(node.type);
    });
  const realAtoms = uniqueRealNodeIds(data, [primarySourceId, ...upstream, ...downstreamContext]);
  return realAtoms.slice(0, Math.max(maxTargets, 1));
}

function focusNodeIdsForSelection(data: V3WorkbenchData, nodeIds: Iterable<string>): string[] {
  return uniqueRealNodeIds(data, nodeIds).slice(0, SELECTION_FIT_NODE_LIMIT);
}

function rowIdSetsEqual(left: ReadonlySet<string> | null, right: ReadonlySet<string> | null): boolean {
  if (left === right) return true;
  if (!left || !right) return false;
  if (left.size !== right.size) return false;
  for (const value of left) {
    if (!right.has(value)) return false;
  }
  return true;
}

function isTsangpoProject(data: V3WorkbenchData): boolean {
  const projectId = data.project?.id?.toLowerCase() ?? "";
  const projectName = data.project?.name?.toLowerCase() ?? "";
  return projectId.includes("tsangpo") || projectName.includes("tsangpo");
}

function projectionModeFromSearch(params: URLSearchParams): ProjectionMode {
  const value = (params.get("projection") ?? params.get("view") ?? "").toLowerCase();
  return value === "correlogram" || value === "correlations" ? "correlogram" : "table";
}

function correlogramToken(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function v3CorrelogramRole(column: ThreadColumn): CorrelogramColumn["role"] {
  const token = correlogramToken(`${column.id} ${column.label}`);
  if (column.kind === "condition") return "condition";
  if (/(alpha|beta|omega|derivative|stability|avl)$/.test(token) || token.includes("alphaavl") || token.includes("betaavl")) return "derived";
  if (column.kind === "quantity") return "response";
  if (/(^|[^a-z])(cl|cd|cy|cf|cm|cn|ld)/.test(token) || /pressure|friction|coefficient/.test(token)) return "response";
  if (/alpha|beta|mach|reynolds|rpm|thrust|elevator|aileron/.test(token)) return "condition";
  return "metadata";
}

function v3CorrelogramEvidence(column: ThreadColumn): CorrelogramColumn["evidence"] {
  const token = correlogramToken(`${column.id} ${column.label}`);
  return /model|surrogate|corrected|transport|predicted|prediction|teacher/.test(token)
    ? "model-conditioned"
    : "observed";
}

function preferredV3CorrelogramZAxis(columns: readonly CorrelogramColumn[]): string | undefined {
  for (const priority of V3_CORRELOGRAM_RESPONSE_PRIORITY) {
    const match = columns.find((column) => (
      correlogramToken(column.key) === priority ||
      correlogramToken(column.header) === priority
    ));
    if (match) return match.key;
  }
  return (
    columns.find((column) => column.role === "response")?.key ??
    columns.find((column) => column.role === "derived")?.key ??
    columns.find((column) => column.role === "condition")?.key ??
    columns[0]?.key
  );
}

function useTsangpoThreadProject(enabled: boolean, projectId: string | null | undefined): Project | null {
  const [project, setProject] = useState<Project | null>(null);
  useEffect(() => {
    if (!enabled || (projectId && UUID_LIKE.test(projectId))) {
      setProject(null);
      return;
    }
    let cancelled = false;
    ensureTsangpoThreadProject()
      .then((result) => {
        if (!cancelled) setProject(result);
      })
      .catch(() => {
        if (!cancelled) setProject(null);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, projectId]);
  return project;
}

function axisLabel(axis: string): string {
  if (axis === "alpha_deg") return "alpha";
  if (axis === "theta_htail_deg") return "theta ht";
  if (axis === "thrust_mult") return "T mult";
  return axis.replace(/_/g, " ");
}

function useTsangpoSpec(enabled: boolean): { spec: TsangpoProjectSpec | null; source: "api" | "fixture" | "none" } {
  const [spec, setSpec] = useState<TsangpoProjectSpec | null>(() => (enabled ? TSANGPO_FALLBACK_SPEC : null));
  const [source, setSource] = useState<"api" | "fixture" | "none">(() => (enabled ? "fixture" : "none"));

  useEffect(() => {
    if (!enabled) {
      setSpec(null);
      setSource("none");
      return;
    }
    let cancelled = false;
    setSpec(TSANGPO_FALLBACK_SPEC);
    setSource("fixture");
    fetchTsangpoProjectSpec()
      .then((next) => {
        if (!cancelled) {
          setSpec(next);
          setSource("api");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSpec(TSANGPO_FALLBACK_SPEC);
          setSource("fixture");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { spec, source };
}

function dagNodeIdsForFilteredRows(data: V3WorkbenchData, filteredRowIds: ReadonlySet<string> | null): Set<string> | null {
  if (!filteredRowIds || filteredRowIds.size >= data.rows.length) return null;

  const rowProjectionByRunId = new Map(data.provenance.rows.map((row) => [row.runId, row]));
  const seedIds = new Set<string>();
  let acceptedRows = 0;

  for (const row of data.rows) {
    if (!filteredRowIds.has(row.runId)) continue;
    acceptedRows += 1;

    const projectionRow = rowProjectionByRunId.get(row.runId);
    for (const sourceNodeId of projectionRow?.sourceNodeIds ?? []) {
      const node = data.provenance.nodesById.get(sourceNodeId);
      if (node && !isProjectionNodeType(node.type)) seedIds.add(sourceNodeId);
    }

    for (const handleId of Object.values(row.cellNodeIds ?? {})) {
      for (const sourceNodeId of sourceNodeIdsForCellHandle(data, handleId)) {
        const node = data.provenance.nodesById.get(sourceNodeId);
        if (node && !isProjectionNodeType(node.type)) seedIds.add(sourceNodeId);
      }
    }

    if (acceptedRows >= FILTERED_DAG_ROW_LIMIT) break;
  }

  const boundedSeeds = [...seedIds].slice(0, FILTERED_DAG_WALK_LIMIT);
  const next = new Set(seedIds);
  for (const nodeId of walkProvenanceUpstream(data.provenance, boundedSeeds, 4, FILTERED_DAG_WALK_LIMIT)) {
    const node = data.provenance.nodesById.get(nodeId);
    if (node && !isProjectionNodeType(node.type)) next.add(nodeId);
  }
  for (const nodeId of walkProvenanceDownstream(data.provenance, boundedSeeds, 3, FILTERED_DAG_WALK_LIMIT)) {
    const node = data.provenance.nodesById.get(nodeId);
    if (node && !isProjectionNodeType(node.type)) next.add(nodeId);
  }

  return next;
}

type V3GridColDef = ColDef<ThreadRow>;
type V3GridColumnDef = V3GridColDef | ColGroupDef<ThreadRow>;

const CONDITION_COLUMN_IDS = new Set(["alpha", "a", "mach", "m", "re", "re_l", "reynolds", "beta", "altitude", "aoa"]);
const SETUP_COLUMN_IDS = new Set(["runId", "family", "design", "mesh", "script", "status", "updated"]);

function normalizedColumnId(id: string): string {
  return id.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_");
}

function isNumericColumn(rows: readonly ThreadRow[], columnId: string): boolean {
  return rows.some((row) => typeof row[columnId] === "number" && Number.isFinite(row[columnId] as number));
}

function aggregationForColumn(column: ThreadColumn, rows: readonly ThreadRow[]): ColDef<ThreadRow>["aggFunc"] | undefined {
  if (column.kind !== "quantity" && !isNumericColumn(rows, column.id)) return undefined;
  const id = normalizedColumnId(column.id);
  const label = normalizedColumnId(column.label);
  if (id === "cd" || id === "c_d" || label.includes("drag")) return "min";
  if (id === "ld" || id === "l_d" || id === "l_over_d" || label.includes("l_d") || label.includes("lift_drag")) return "max";
  if (id === "cl_max" || label.includes("cl_max")) return "max";
  if (id === "cl_min" || label.includes("cl_min")) return "min";
  return "avg";
}

function columnGroupForThreadColumn(column: ThreadColumn): "setup" | "conditions" | "outputs" | "artifacts" | "metadata" {
  const id = normalizedColumnId(column.id);
  if (column.kind === "condition" || CONDITION_COLUMN_IDS.has(id)) return "conditions";
  if (column.kind === "quantity") return "outputs";
  if (column.kind === "asset") return "artifacts";
  if (column.kind === "id" || column.kind === "family" || SETUP_COLUMN_IDS.has(id)) return "setup";
  return "metadata";
}

function defaultRowGroupForColumn(column: ThreadColumn): boolean {
  const id = normalizedColumnId(column.id);
  return column.kind === "family" || id === "family" || id === "model" || id === "model_family";
}

function columnsForVisibleGrid(columns: readonly ThreadColumn[]): ThreadColumn[] {
  const byGroup = {
    setup: columns.filter((column) => columnGroupForThreadColumn(column) === "setup"),
    conditions: columns.filter((column) => columnGroupForThreadColumn(column) === "conditions"),
    outputs: columns.filter((column) => columnGroupForThreadColumn(column) === "outputs"),
    artifacts: columns.filter((column) => columnGroupForThreadColumn(column) === "artifacts"),
    metadata: columns.filter((column) => columnGroupForThreadColumn(column) === "metadata"),
  };
  return [
    ...byGroup.setup.slice(0, 6),
    ...byGroup.conditions.slice(0, 8),
    ...byGroup.outputs.slice(0, 10),
    ...byGroup.artifacts.slice(0, 2),
    ...byGroup.metadata.slice(0, 4),
  ];
}

function columnsForGrid(
  columns: readonly ThreadColumn[],
  rows: readonly ThreadRow[],
  selectionRef: { current: ThreadSelectionState },
  bridges: V3WorkbenchData["bridges"],
): V3GridColumnDef[] {
  const visibleColumns = columnsForVisibleGrid(columns);

  const columnDefs = visibleColumns.map((column) => {
    const initialAggFunc = aggregationForColumn(column, rows) ?? undefined;
    return {
      field: column.id,
      headerName: column.label,
      sortable: true,
      filter: true,
      resizable: true,
      enableRowGroup: column.kind !== "quantity" && column.kind !== "asset",
      enableValue: column.kind === "quantity" || isNumericColumn(rows, column.id),
      initialRowGroup: defaultRowGroupForColumn(column),
      initialHide: defaultRowGroupForColumn(column),
      minWidth: column.kind === "id" ? 150 : column.kind === "quantity" ? 108 : 96,
      type: isNumericColumn(rows, column.id) ? "numericColumn" : undefined,
      initialAggFunc,
      allowedAggFuncs: isNumericColumn(rows, column.id) ? ["avg", "min", "max", "sum", "count"] : ["count"],
      headerClass: `v3-grid-header-${columnGroupForThreadColumn(column)}`,
      cellClass: (params) => {
        const selection = selectionRef.current;
        const primary = selection.primary;
        const selectedRow =
          primary.kind === "cell" || primary.kind === "row"
            ? rows.find((row) => row.runId === primary.rowId) ?? null
            : null;
        const selectedNodeId = selection.primary.kind === "cell" ? selection.primary.nodeId : null;
        if (!params.data || !selectedRow) return `v3-grid-cell-${columnGroupForThreadColumn(column)}`;
        const compatibility = compatibilityClassName(classifyCompatibility({
          row: params.data,
          column,
          selectedRow,
          selection,
          bridges,
        }));
        const nodeSelected = selectedNodeId && params.data.cellNodeIds?.[column.id] === selectedNodeId ? " v3-cell-node-selected" : "";
        return `v3-grid-cell-${columnGroupForThreadColumn(column)} ${compatibility}${nodeSelected}`;
      },
      valueFormatter: (params: ValueFormatterParams<ThreadRow, unknown>) => displayValue(params.value),
      cellRenderer: (params: ICellRendererParams<ThreadRow, unknown>) => {
        const nodeId = params.data?.cellNodeIds?.[column.id];
        const rowNodeId = params.data?.rowNodeId;
        return (
          <span
            className="v3-cell-handle"
            data-node-id={nodeId}
            data-row-node-id={rowNodeId}
            data-row-id={params.data?.runId}
            data-col-id={column.id}
            title={nodeId ? `${nodeId} -> ${column.label}` : column.label}
          >
            {displayValue(params.value)}
          </span>
        );
      },
    } satisfies ColDef<ThreadRow>;
  });

  const columnById = new Map(visibleColumns.map((column) => [column.id, column]));
  const groupForDef = (column: V3GridColDef) => columnGroupForThreadColumn(
    columnById.get(String(column.field)) ?? { id: String(column.field), label: String(column.headerName), kind: "metadata" },
  );
  const byGroup = {
    setup: columnDefs.filter((column) => groupForDef(column) === "setup"),
    conditions: columnDefs.filter((column) => groupForDef(column) === "conditions"),
    outputs: columnDefs.filter((column) => groupForDef(column) === "outputs"),
    artifacts: columnDefs.filter((column) => groupForDef(column) === "artifacts"),
    metadata: columnDefs.filter((column) => groupForDef(column) === "metadata"),
  };

  const groups: V3GridColumnDef[] = [];
  const addGroup = (headerName: string, headerClass: string, children: V3GridColDef[]) => {
    if (children.length) groups.push({ headerName, headerClass, marryChildren: true, children } satisfies ColGroupDef<ThreadRow>);
  };
  addGroup("Conditions / pivot rails", "v3-grid-group-conditions", byGroup.conditions);
  addGroup("Outputs / derived quantities", "v3-grid-group-outputs", byGroup.outputs);
  addGroup("Run + model", "v3-grid-group-setup", byGroup.setup);
  addGroup("Artifacts", "v3-grid-group-artifacts", byGroup.artifacts);
  addGroup("Metadata", "v3-grid-group-metadata", byGroup.metadata);
  return groups;
}

function rowClass(row: ThreadRow, selection: ThreadSelectionState, litRowIds: ReadonlySet<string>): string {
  if (selection.primary.kind === "row" && selection.primary.rowId === row.runId) return "v3-row-selected";
  if (selection.primary.kind === "cell" && selection.primary.rowId === row.runId) return "v3-row-selected";
  if (litRowIds.has(row.runId)) return "v3-row-lit";
  return "";
}

function selectionNarrative(selection: ThreadSelectionState, data: V3WorkbenchData): string {
  const primary = selection.primary;
  if (primary.kind === "cell") {
    const column = data.columns.find((item) => item.id === primary.colId);
    return `${primary.rowId} · ${column?.label ?? primary.colId} is a live handle into source, condition, model, correction, review, and downstream claims.`;
  }
  if (primary.kind === "row") {
    return `${primary.rowId} is a provenance subgraph combed into one readable row.`;
  }
  if (primary.kind === "asset") {
    const asset = data.assets.find((item) => item.id === primary.assetId);
    return `${asset?.label ?? "This asset"} is a projection of the same record; its source rows and cells stay traceable.`;
  }
  return "Pick a cell, row, or asset to pull one provenance strand out of the combed table.";
}

function V3ScaleMetrics({
  data,
  activePivot,
  filteredRowCount,
  filterActive,
}: {
  data: V3WorkbenchData;
  activePivot: V3PivotView | null;
  filteredRowCount: number;
  filterActive: boolean;
}) {
  const totalNodes = data.provenance.representedScale.totalNodes || data.provenance.nodes.length;
  const totalEdges = data.provenance.representedScale.totalEdges || data.provenance.edges.length;
  const loadedRows = data.rows.length;
  const representedRows = Math.max(data.totalRowCount ?? loadedRows, loadedRows);
  const rowValue = filterActive
    ? `${compactCount(filteredRowCount)}/${compactCount(loadedRows)}`
    : representedRows > loadedRows
      ? `${compactCount(loadedRows)}/${compactCount(representedRows)}`
      : compactCount(loadedRows);
  const rowTitle = filterActive
    ? `${filteredRowCount.toLocaleString()} of ${loadedRows.toLocaleString()} loaded rows after AG Grid filters${representedRows > loadedRows ? `; ${representedRows.toLocaleString()} rows represented by the source` : ""}`
    : representedRows > loadedRows
      ? `${loadedRows.toLocaleString()} rows loaded into the workbench; ${representedRows.toLocaleString()} rows represented by the source`
      : `${loadedRows.toLocaleString()} rows`;
  const metrics = [
    { label: "nodes", value: totalNodes },
    { label: "edges", value: totalEdges },
    { label: "cols", value: data.columns.length },
    { label: "assets", value: data.assets.length },
    { label: "pivot rows", value: activePivot?.compatibleRowIds.length ?? 0 },
  ];
  return (
    <div className="v3-scale-metrics" data-testid="v3-scale-metrics" aria-label="Workbench scale metrics">
      <span
        className={filterActive ? "is-filtered" : ""}
        title={rowTitle}
      >
        <strong>{rowValue}</strong>
        rows
      </span>
      {metrics.map((metric) => (
        <span key={metric.label} title={`${metric.value.toLocaleString()} ${metric.label}`}>
          <strong>{compactCount(metric.value)}</strong>
          {metric.label}
        </span>
      ))}
    </div>
  );
}

function projectCreatedLabel(project: Project): string {
  if (!project.created_at) return "local";
  const date = new Date(project.created_at);
  if (Number.isNaN(date.getTime())) return "local";
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

function projectBranch(project: Project): string | null {
  const metadata = project.metadata;
  const branch = metadata && typeof metadata.branch === "string" ? metadata.branch.trim() : "";
  return branch || null;
}

function projectSource(project: Project): string {
  const source = project.metadata && typeof project.metadata.source === "string" ? project.metadata.source : "";
  if (source === "local_x57_csv") return "X-57 local";
  if (source === "sidecar") return "Sidecar";
  if (source === "local_empty") return "Empty";
  return source || "Thread";
}

type V3ProjectDirectoryState = {
  projects: Project[];
  loading: boolean;
  error: string | null;
  creating: boolean;
  reload: () => void;
  create: (name: string) => Promise<Project | null>;
};

function useV3ProjectDirectory(active: boolean): V3ProjectDirectoryState {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void fetchProjects()
      .then((items) => {
        if (!cancelled) setProjects(items);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active, reloadToken]);

  const create = useCallback(async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return null;
    setCreating(true);
    setError(null);
    try {
      const project = await createProject(trimmed);
      setProjects((current) => [project, ...current.filter((item) => item.id !== project.id)]);
      return project;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return null;
    } finally {
      setCreating(false);
    }
  }, []);

  return {
    projects,
    loading,
    error,
    creating,
    reload: () => setReloadToken((value) => value + 1),
    create,
  };
}

function V3ProjectSwitcher({
  currentProject,
  currentBranch,
  open,
  directory,
  draftName,
  onDraftNameChange,
  onToggle,
  onSelectProject,
  onCreateProject,
}: {
  currentProject: V3WorkbenchData["project"];
  currentBranch: string;
  open: boolean;
  directory: V3ProjectDirectoryState;
  draftName: string;
  onDraftNameChange: (value: string) => void;
  onToggle: () => void;
  onSelectProject: (project: Project) => void;
  onCreateProject: () => void;
}) {
  const currentLabel = currentProject?.name ?? "No project";
  return (
    <div className="v3-project-switcher" data-testid="v3-project-switcher">
      <button
        type="button"
        className={open ? "active" : ""}
        data-testid="v3-project-switcher-toggle"
        onClick={onToggle}
      >
        <span>Project</span>
        <strong>{currentLabel}</strong>
      </button>
      {open && (
        <section className="v3-project-menu" data-testid="v3-project-menu" aria-label="Project directory">
          <header>
            <div>
              <span>Project view</span>
              <strong>{currentLabel}</strong>
              <small>{currentBranch}</small>
            </div>
            <button type="button" onClick={directory.reload}>Refresh</button>
          </header>
          <form
            className="v3-project-create"
            onSubmit={(event) => {
              event.preventDefault();
              onCreateProject();
            }}
          >
            <input
              value={draftName}
              placeholder="New Thread project"
              onChange={(event) => onDraftNameChange(event.currentTarget.value)}
            />
            <button type="submit" disabled={directory.creating || !draftName.trim()}>
              {directory.creating ? "Creating" : "Create"}
            </button>
          </form>
          {directory.error && <p className="v3-project-error">{directory.error}</p>}
          <div className="v3-project-list">
            {directory.loading && <div className="v3-project-empty">Loading projects…</div>}
            {!directory.loading && directory.projects.length === 0 && <div className="v3-project-empty">No projects yet.</div>}
            {directory.projects.map((project) => {
              const selected = project.id === currentProject?.id;
              return (
                <button
                  key={project.id}
                  type="button"
                  className={selected ? "selected" : ""}
                  data-testid={`v3-project-option-${project.id}`}
                  onClick={() => onSelectProject(project)}
                >
                  <span>
                    <strong>{project.name}</strong>
                    <small>{projectSource(project)} · {projectBranch(project) ?? "main"} · {projectCreatedLabel(project)}</small>
                  </span>
                  <i>{selected ? "open" : "view"}</i>
                </button>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

function topologySliceClusterIds(
  data: V3WorkbenchData,
  derived: DerivedThreadSelection,
  selection: ThreadSelectionState,
): Set<string> {
  const next = new Set(derived.litClusterIds);
  const primary = selection.primary;
  if (primary.kind === "cell" || primary.kind === "row") {
    const preferred = ["source", "design", "script", "mesh", "run", "operation", "table", "analysis_table"];
    for (const cluster of data.clusters) {
      if (preferred.includes(cluster.id) || preferred.includes(cluster.type)) next.add(cluster.id);
    }
    for (const asset of data.assets) {
      const touchesRow = asset.refs.rows?.includes(primary.rowId) ?? false;
      const touchesCell = primary.kind === "cell"
        ? asset.refs.cells?.some((cell) => cell.runId === primary.rowId && cell.col === primary.colId) ?? false
        : false;
      if (touchesRow || touchesCell) {
        for (const upstream of asset.refs.upstream ?? []) next.add(upstream);
      }
    }
  }
  if (primary.kind === "asset") {
    const asset = data.assets.find((item) => item.id === primary.assetId);
    for (const upstream of asset?.refs.upstream ?? []) next.add(upstream);
  }
  return next;
}

function V3TopologyLayer({
  data,
  litClusterIds,
  litNodeIds,
  selectedNodeId,
  filteredNodeIds,
  filteredClusterIds,
  filterActive,
  sliceActive,
  variant,
  onFocusChange,
  onSelectNode,
  onComb,
  projectionDirection,
  dagLayout,
  deckViewState,
  onDeckViewStateChange,
  onDagLayoutChange,
  onDagLayoutReset,
  onMoveDagNode,
  onDropDagNode,
  onMoveDagCluster,
  onDeepInspect,
}: {
  data: V3WorkbenchData;
  litClusterIds: Set<string>;
  litNodeIds: ReadonlySet<string>;
  selectedNodeId: string | null;
  filteredNodeIds: ReadonlySet<string> | null;
  filteredClusterIds: ReadonlySet<string>;
  filterActive: boolean;
  sliceActive: boolean;
  variant: V3Variant;
  onFocusChange: (focused: boolean) => void;
  onSelectNode: (nodeId: string) => void;
  onComb: () => void;
  projectionDirection: V3ProjectionDirection;
  dagLayout: V3DagLayoutState;
  deckViewState: V3DeckViewState;
  onDeckViewStateChange: (viewState: V3DeckViewState) => void;
  onDagLayoutChange: (patch: Partial<V3DagLayoutState> | ((layout: V3DagLayoutState) => V3DagLayoutState)) => void;
  onDagLayoutReset: () => void;
  onMoveDagNode: (nodeId: string, position: V3DagPinnedPosition) => void;
  onDropDagNode: (nodeId: string, position: V3DagPinnedPosition) => void;
  onMoveDagCluster: (clusterId: string, position: V3DagPinnedPosition) => void;
  onDeepInspect: () => void;
}) {
  const graph = data.provenance;
  const litIds = useMemo(() => new Set(litNodeIds), [litNodeIds]);
  const visibleNodes = useMemo(() => {
    const perCluster = new Map<string, number>();
    const sampled = graph.nodes.filter((node) => {
      if (isProjectionNodeType(node.type)) return false;
      if (filterActive && filteredNodeIds?.has(node.id)) return true;
      if (litIds.has(node.id) || node.id === selectedNodeId) return true;
      const count = perCluster.get(node.cluster) ?? 0;
      const budget = filterActive ? (sliceActive ? 24 : 14) : sliceActive ? 44 : 28;
      if (count >= budget) return false;
      perCluster.set(node.cluster, count + 1);
      return true;
    });
    return sampled
      .sort((left, right) => {
        const leftPriority = (left.id === selectedNodeId ? 4 : 0) + (litIds.has(left.id) ? 3 : 0) + (filteredNodeIds?.has(left.id) ? 2 : 0);
        const rightPriority = (right.id === selectedNodeId ? 4 : 0) + (litIds.has(right.id) ? 3 : 0) + (filteredNodeIds?.has(right.id) ? 2 : 0);
        return rightPriority - leftPriority;
      })
      .slice(0, filterActive ? 900 : sliceActive ? 760 : 420);
  }, [filterActive, filteredNodeIds, graph.nodes, litIds, selectedNodeId, sliceActive]);
  const visibleNodeIds = useMemo(() => new Set(visibleNodes.map((node) => node.id)), [visibleNodes]);
  const visibleEdges = useMemo(() => {
    const edges: typeof graph.edges = [];
    let ambient = 0;
    const ambientBudget = filterActive ? 220 : sliceActive ? 72 : 360;
    for (const edge of graph.edges) {
      const source = graph.nodesById.get(edge.from);
      const target = graph.nodesById.get(edge.to);
      if (!source || !target || isProjectionNodeType(source.type) || isProjectionNodeType(target.type)) continue;
      const lit = litIds.has(edge.from) && litIds.has(edge.to);
      const inFilter = Boolean(filterActive && filteredNodeIds?.has(edge.from) && filteredNodeIds.has(edge.to));
      if (!lit && !inFilter && (!visibleNodeIds.has(edge.from) || !visibleNodeIds.has(edge.to))) continue;
      if (!lit && !inFilter && ambient > ambientBudget) continue;
      if (!lit && !inFilter) ambient += 1;
      edges.push(edge);
    }
    return edges;
  }, [filterActive, filteredNodeIds, graph.edges, graph.nodesById, litIds, sliceActive, visibleNodeIds]);
  const zoomDeck = (delta: number) => {
    onDeckViewStateChange({
      ...deckViewState,
      zoom: clampNumber(deckViewState.zoom + delta, -1.5, 6),
    });
  };

  return (
    <section
      className="v3-topology-plane v3-redux-topology"
      data-testid="v3-topology-plane"
      tabIndex={0}
      aria-label="Identity-preserving provenance DAG topology overview"
      onMouseEnter={() => onFocusChange(true)}
      onMouseLeave={() => onFocusChange(false)}
      onFocus={() => onFocusChange(true)}
      onBlur={() => onFocusChange(false)}
    >
      <div className="v3-topology-meta">
        <span>Global DAG · source record</span>
        <span>{variant.short}</span>
        <span>{graph.nodes.length.toLocaleString()} positioned nodes</span>
        <span>{graph.edges.length.toLocaleString()} edges</span>
        {filterActive && <span>{(filteredNodeIds?.size ?? 0).toLocaleString()} nodes in filtered rows</span>}
        <span>Projection: {visibleNodes.length.toLocaleString()} visible / {graph.representedScale.totalNodes.toLocaleString()} total</span>
      </div>
      <div className="v3-topology-actions">
        <button type="button" onClick={onComb} disabled={projectionDirection !== null}>
          {projectionDirection ? "Tracing" : "Trace projection"}
        </button>
        <button type="button" onClick={() => zoomDeck(0.65)}>Zoom +</button>
        <button type="button" onClick={() => zoomDeck(-0.65)}>Zoom -</button>
        <button type="button" onClick={() => onDeckViewStateChange({ target: [640, 310, 0], zoom: 0 })}>Reset View</button>
        <button
          type="button"
          data-testid="v3-dag-depth-toggle"
          onClick={() => onDagLayoutChange((layout) => ({ ...layout, projectionMode: layout.projectionMode === "depth" ? "plan" : "depth" }))}
        >
          {dagLayout.projectionMode === "depth" ? "Plan view" : "Depth view"}
        </button>
        <button type="button" data-testid="v3-dag-layout-reset" onClick={onDagLayoutReset}>Reset Layout</button>
        <button type="button" onClick={onDeepInspect}>Inspect deeper</button>
      </div>
      <div className="v3-dag-depth-controls" data-testid="v3-dag-depth-controls">
        <label>
          <span>Depth</span>
          <input
            aria-label="DAG depth"
            type="range"
            min="0"
            max="1.25"
            step="0.05"
            value={dagLayout.depthStrength}
            onChange={(event) => onDagLayoutChange({ depthStrength: Number(event.currentTarget.value), projectionMode: "depth" })}
          />
        </label>
        <label>
          <span>Perspective</span>
          <input
            aria-label="DAG perspective"
            type="range"
            min="0"
            max="0.8"
            step="0.04"
            value={dagLayout.perspective}
            onChange={(event) => onDagLayoutChange({ perspective: Number(event.currentTarget.value), projectionMode: "depth" })}
          />
        </label>
        <span>{Object.keys(dagLayout.nodePins).length} nodes pinned · {Object.keys(dagLayout.clusterPins).length} groups moved · {Object.keys(dagLayout.memberships).length} multi-group nodes</span>
      </div>
      <div className="v3-topology-reveal">Deck.gl WebGL DAG · drag canvas to pan · scroll to zoom · drag nodes or halos to pin this projection</div>
      <V3DeckDagLayer
        graph={graph}
        visibleNodes={visibleNodes}
        visibleEdges={visibleEdges}
        litNodeIds={litIds}
        litClusterIds={litClusterIds}
        filteredNodeIds={filteredNodeIds}
        filteredClusterIds={filteredClusterIds}
        filterActive={filterActive}
        selectedNodeId={selectedNodeId}
        sliceActive={sliceActive}
        layout={dagLayout}
        viewState={deckViewState}
        onViewStateChange={onDeckViewStateChange}
        onSelectNode={onSelectNode}
        onMoveNode={onMoveDagNode}
        onDropNode={onDropDagNode}
        onMoveCluster={onMoveDagCluster}
      />
    </section>
  );
}

function V3DataGrid({
  data,
  selection,
  litRowIds,
  onSelectionChange,
  onPulseToNode,
  onFilterRowsChange,
}: {
  data: V3WorkbenchData;
  selection: ThreadSelectionState;
  litRowIds: ReadonlySet<string>;
  onSelectionChange: (selection: ThreadSelectionState) => void;
  onPulseToNode: (source: HTMLElement | null, nodeId: string, kind?: string) => void;
  onFilterRowsChange: (rowIds: Set<string> | null) => void;
}) {
  const gridTheme = useMemo(
    () => themeQuartz.withPart(colorSchemeDark).withParams({
      accentColor: "#5ec8e0",
      backgroundColor: "rgba(10, 12, 16, 0.54)",
      borderColor: "#232b35",
      browserColorScheme: "dark",
      cellHorizontalPadding: 10,
      chromeBackgroundColor: "rgba(14, 18, 23, 0.68)",
      fontFamily: "IBM Plex Mono, JetBrains Mono, ui-monospace, SFMono-Regular, Menlo, monospace",
      foregroundColor: "#d7dfe8",
      headerBackgroundColor: "rgba(18, 23, 30, 0.72)",
      headerFontSize: 11,
      rowHoverColor: "rgba(94, 200, 224, 0.08)",
      wrapperBorderRadius: 0,
    }),
    [],
  );
  const selectionRef = useRef(selection);
  useEffect(() => {
    selectionRef.current = selection;
  }, [selection]);
  const [advancedFilterParent, setAdvancedFilterParent] = useState<HTMLDivElement | null>(null);
  const lastPublishedFilterRef = useRef<string>("all");
  const columnDefs = useMemo(
    () => columnsForGrid(data.columns, data.rows, selectionRef, data.bridges),
    [data.bridges, data.columns, data.rows],
  );
  const publishFilteredRows = useCallback((api: GridApi<ThreadRow>) => {
    const rowIds = new Set<string>();
    api.forEachNodeAfterFilterAndSort((node) => {
      if (node.data?.runId) rowIds.add(node.data.runId);
    });
    const next = api.isAnyFilterPresent() ? rowIds : null;
    const signature = next ? [...next].sort().join("\u001f") : "all";
    if (signature === lastPublishedFilterRef.current) return;
    lastPublishedFilterRef.current = signature;
    onFilterRowsChange(next);
  }, [onFilterRowsChange]);

  return (
    <div className="v3-grid-wrap" data-testid="v3-ag-grid">
      <div className="v3-grid-toolbar" data-testid="v3-grid-toolbar">
        <span>Advanced Filter</span>
        <div
          ref={setAdvancedFilterParent}
          className="v3-advanced-filter-host"
          data-testid="v3-advanced-filter-host"
        />
      </div>
      <AgGridReact<ThreadRow>
        theme={gridTheme}
        rowData={data.rows}
        columnDefs={columnDefs}
        enableAdvancedFilter={true}
        includeHiddenColumnsInAdvancedFilter={true}
        advancedFilterParent={advancedFilterParent}
        getRowId={(params) => params.data.runId}
        getRowClass={(params) => params.data ? rowClass(params.data, selection, litRowIds) : ""}
        defaultColDef={{
          suppressMovable: false,
          cellDataType: false,
          enableRowGroup: true,
          enableValue: true,
        }}
        autoGroupColumnDef={{
          headerName: "Grouped rows",
          minWidth: 180,
          pinned: "left",
          cellRendererParams: { suppressCount: false },
        }}
        groupDisplayType="singleColumn"
        groupDefaultExpanded={1}
        rowGroupPanelShow="always"
        suppressAggFuncInHeader={false}
        maintainColumnOrder={true}
        rowSelection={{
          mode: "singleRow",
          checkboxes: false,
          enableClickSelection: true,
        }}
        animateRows={false}
        suppressCellFocus={false}
        onGridReady={(event: GridReadyEvent<ThreadRow>) => publishFilteredRows(event.api)}
        onFilterChanged={(event: FilterChangedEvent<ThreadRow>) => publishFilteredRows(event.api)}
        onModelUpdated={(event: ModelUpdatedEvent<ThreadRow>) => publishFilteredRows(event.api)}
        onCellClicked={(event: CellClickedEvent<ThreadRow>) => {
          const colId = event.column.getColId();
          if (!event.data || !colId) return;
          const nodeId = event.data.cellNodeIds?.[colId];
          onSelectionChange({ primary: { kind: "cell", rowId: event.data.runId, colId, nodeId } });
          onPulseToNode((event.event?.target as HTMLElement | null)?.closest("[data-node-id]") ?? null, nodeId ?? "");
        }}
      />
    </div>
  );
}

function V3Correlogram({
  data,
  selection,
  filteredRowIds,
  filterActive,
  onSelectionChange,
}: {
  data: V3WorkbenchData;
  selection: ThreadSelectionState;
  filteredRowIds: ReadonlySet<string> | null;
  filterActive: boolean;
  onSelectionChange: (selection: ThreadSelectionState) => void;
}) {
  const rows = useMemo(
    () => filteredRowIds ? data.rows.filter((row) => filteredRowIds.has(row.runId)) : data.rows,
    [data.rows, filteredRowIds],
  );
  const correlogramRows = rows as unknown as Record<string, unknown>[];
  const allNumericColumns = useMemo<CorrelogramColumn[]>(() => {
    return data.columns
      .filter((column) => rows.some((row) => typeof row[column.id] === "number" && Number.isFinite(row[column.id] as number)))
      .map((column) => {
        const role = v3CorrelogramRole(column);
        const evidence = v3CorrelogramEvidence(column);
        return {
          key: column.id,
          header: role === "condition" ? `${column.label} rail` : column.label,
          role,
          evidence,
          source: evidence === "model-conditioned" ? "model" : role === "derived" ? "derived" : "observed",
        };
      });
  }, [data.columns, rows]);
  const zAxisKey = useMemo(() => preferredV3CorrelogramZAxis(allNumericColumns), [allNumericColumns]);
  const numericColumns = useMemo<CorrelogramColumn[]>(() => {
    if (!zAxisKey || allNumericColumns.length <= V3_CORRELOGRAM_MAX_COLUMNS) return allNumericColumns;
    const byKey = new Map(allNumericColumns.map((column) => [column.key, column]));
    const ranked = rankColumnsByCorrelationTo(
      correlogramRows,
      allNumericColumns.map((column) => ({ field: column.key, headerName: column.header })),
      zAxisKey,
    );
    const rankedFields = ranked.map((score) => score.field);
    const conditionKeys = allNumericColumns
      .filter((column) => column.role === "condition" && column.key !== zAxisKey)
      .sort((left, right) => {
        const leftRank = rankedFields.indexOf(left.key);
        const rightRank = rankedFields.indexOf(right.key);
        return (leftRank === -1 ? Number.MAX_SAFE_INTEGER : leftRank) - (rightRank === -1 ? Number.MAX_SAFE_INTEGER : rightRank);
      })
      .map((column) => column.key)
      .slice(0, 4);
    const responseKeys = rankedFields
      .filter((field) => !conditionKeys.includes(field))
      .slice(0, Math.max(0, V3_CORRELOGRAM_MAX_COLUMNS - 1 - conditionKeys.length));
    const orderedKeys = [zAxisKey, ...conditionKeys, ...responseKeys, ...allNumericColumns.map((column) => column.key)];
    const unique = [...new Set(orderedKeys)]
      .map((key) => byKey.get(key))
      .filter((column): column is CorrelogramColumn => Boolean(column))
      .slice(0, V3_CORRELOGRAM_MAX_COLUMNS);
    return unique;
  }, [allNumericColumns, correlogramRows, zAxisKey]);
  const zAxisLabel = zAxisKey ? numericColumns.find((column) => column.key === zAxisKey)?.header ?? zAxisKey : undefined;
  const selectedRowId = selection.primary.kind === "cell" || selection.primary.kind === "row" ? selection.primary.rowId : null;
  const selectedRowIdx = selectedRowId ? rows.findIndex((row) => row.runId === selectedRowId) : -1;
  const groups = useMemo(() => {
    const grouped = new Map<string, Record<string, unknown>[]>();
    for (const row of correlogramRows) {
      const key = typeof row.family === "string" && row.family ? row.family : "unknown";
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(row);
    }
    return [...grouped.entries()].map(([key, groupRows], index) => {
      const family = data.families[key];
      return {
        key,
        label: family?.label ?? key,
        rows: groupRows,
        color: V3_CORRELOGRAM_GROUP_COLORS[index % V3_CORRELOGRAM_GROUP_COLORS.length]!,
      };
    });
  }, [correlogramRows, data.families]);
  const quantityCount = numericColumns.filter((column) => column.role === "response" || column.role === "derived").length;
  const conditionCount = numericColumns.filter((column) => column.role === "condition").length;
  const hiddenNumericCount = Math.max(0, allNumericColumns.length - numericColumns.length);
  const projectId = data.project?.id ?? "thread-v3";

  if (!rows.length || numericColumns.length < 2) {
    return (
      <div className="v3-correlogram v3-correlogram-empty" data-testid="v3-correlogram">
        <p className="v3-eyebrow">Correlogram projection</p>
        <h2>No plottable correlation view</h2>
        <p>Thread needs at least two numeric condition/output columns in the active pivot before it can render the existing data-explorer correlogram.</p>
      </div>
    );
  }

  return (
    <div className="v3-correlogram" data-testid="v3-correlogram">
      <div className="v3-correlogram-header">
        <div>
          <p className="v3-eyebrow">Correlogram projection</p>
          <h2>Thread data-explorer correlogram</h2>
        </div>
        <div className="v3-correlogram-stats">
          <span>{filterActive ? `${rows.length.toLocaleString()} / ${data.rows.length.toLocaleString()} filtered rows` : `${rows.length.toLocaleString()} rows`}</span>
          <span>{conditionCount} conditions</span>
          <span>{quantityCount} outputs</span>
          {zAxisLabel ? <span>Z {zAxisLabel}</span> : null}
          {hiddenNumericCount ? <span>{hiddenNumericCount} ranked off-screen</span> : null}
          <span>{groups.length} groups</span>
        </div>
      </div>
      <div className="v3-correlogram-host" data-testid="v3-existing-correlogram-host">
        <ProvenanceProvider projectId={projectId}>
          <Suspense fallback={<div className="v3-correlogram-loading">Loading correlogram projection…</div>}>
            <DataExplorerCorrelogram
              rows={correlogramRows}
              numericColumns={numericColumns}
              groups={groups.length > 1 ? groups : undefined}
              colorByKey={groups.length > 1 ? undefined : "family"}
              zAxisKey={zAxisKey}
              selectedRowIdx={selectedRowIdx >= 0 ? selectedRowIdx : undefined}
              onPointClick={(rowIdx) => {
                const row = rows[rowIdx];
                if (!row) return;
                onSelectionChange({ primary: { kind: "row", rowId: row.runId, nodeId: row.rowNodeId } });
              }}
            />
          </Suspense>
        </ProvenanceProvider>
      </div>
    </div>
  );
}

function V3ProjectionStage({
  data,
  projection,
  selection,
  litRowIds,
  filteredRowIds,
  filteredRowCount,
  filterActive,
  onProjectionChange,
  onSelectionChange,
  onPulseToNode,
  onFilterRowsChange,
}: {
  data: V3WorkbenchData;
  projection: ProjectionMode;
  selection: ThreadSelectionState;
  litRowIds: ReadonlySet<string>;
  filteredRowIds: ReadonlySet<string> | null;
  filteredRowCount: number;
  filterActive: boolean;
  onProjectionChange: (projection: ProjectionMode) => void;
  onSelectionChange: (selection: ThreadSelectionState) => void;
  onPulseToNode: (source: HTMLElement | null, nodeId: string, kind?: string) => void;
  onFilterRowsChange: (rowIds: Set<string> | null) => void;
}) {
  const selectedColumn = selection.primary.kind === "cell" ? selection.primary.colId : data.columns[0]?.id ?? "runId";
  const selectedRow = selection.primary.kind === "cell" || selection.primary.kind === "row"
    ? selection.primary.rowId
    : data.rows[0]?.runId ?? null;
  const narrative = selectionNarrative(selection, data);

  return (
    <section className="v3-stage">
      <div className="v3-stage-head">
        <div>
          <p className="v3-eyebrow">Combed DAG projection</p>
          <h1>{data.project?.name ?? "Thread project"} · {data.branch}</h1>
          <p className="v3-stage-subtitle">The table is the DAG, combed flat around condition rails.</p>
        </div>
        <div className="v3-switch" role="tablist" aria-label="Projection view">
          <button className={projection === "table" ? "active" : ""} onClick={() => onProjectionChange("table")}>Table</button>
          <button className={projection === "correlogram" ? "active" : ""} onClick={() => onProjectionChange("correlogram")}>Correlogram</button>
        </div>
      </div>
      <div className="v3-comb-rail" aria-label="DAG comb projection path">
        <span>DAG</span>
        <i />
        <span>condition_key rails</span>
        <i />
        <span>{projection === "table" ? "flat table" : "correlogram view"}</span>
        <i />
        <span>claims + assets</span>
      </div>
      <div className="v3-stage-body">
        {projection === "table" ? (
          <V3DataGrid
            data={data}
            selection={selection}
            litRowIds={litRowIds}
            onSelectionChange={onSelectionChange}
            onPulseToNode={onPulseToNode}
            onFilterRowsChange={onFilterRowsChange}
          />
        ) : (
          <V3Correlogram
            data={data}
            selection={selection}
            filteredRowIds={filteredRowIds}
            filterActive={filterActive}
            onSelectionChange={onSelectionChange}
          />
        )}
      </div>
      <div className="v3-stage-foot">
        <span>{narrative}</span>
        <span>selected row · {selectedRow ?? "none"}</span>
        <span>column · {selectedColumn}</span>
        <span>
          {filterActive
            ? `${filteredRowCount.toLocaleString()} / ${data.rows.length.toLocaleString()} loaded rows after filters`
            : data.totalRowCount > data.rows.length
              ? `${data.rows.length.toLocaleString()} / ${data.totalRowCount.toLocaleString()} rows loaded`
              : `${data.rows.length.toLocaleString()} rows`}
        </span>
        <span>{data.source}</span>
      </div>
    </section>
  );
}

function assetIcon(asset: ThreadAsset): string {
  const icons: Record<string, string> = {
    plot: "⌁",
    deck: "▣",
    chat: "◌",
    email: "@",
    report: "▤",
    notebook: "⌘",
    spreadsheet: "▥",
    uvf: "◫",
    "table-view": "▦",
    "correlogram-view": "✣",
    decision: "✓",
    storyboard: "▧",
  };
  return icons[asset.type] ?? "◆";
}

function sourceLabel(source: ThreadAsset["source"]): string {
  const labels: Record<ThreadAsset["source"], string> = {
    aeronauty: "Aeronauty",
    cms: "CMS",
    email: "Email",
    excel: "Excel",
    figma: "Figma",
    "google-sheets": "Google Sheets",
    "google-slides": "Google Slides",
    jupyter: "Jupyter",
    pdf: "PDF",
    powerpoint: "PowerPoint",
    python: "Python",
    runway: "Runway",
    sharepoint: "SharePoint",
    slack: "Slack",
    thread: "Thread",
    uvf: "UVF",
    web: "Web",
  };
  return labels[source] ?? source;
}

function githubRepoFromUrl(url: string | undefined): { owner: string; name: string } | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    if (parsed.hostname.toLowerCase() !== "github.com") return null;
    const [owner, name] = parsed.pathname.replace(/^\/+/, "").split("/");
    if (!owner || !name) return null;
    return { owner, name: name.replace(/\.git$/, "") };
  } catch {
    return null;
  }
}

function tsangpoCampaignScriptPath(campaign: TsangpoCampaignSpec): string {
  const postprocessMatch = campaign.postprocess.match(/\/post\/([^/]+)\/([^/]+)$/);
  if (postprocessMatch) {
    const [, familyDir, phaseDir] = postprocessMatch;
    return `post/${familyDir}/plot_${phaseDir}_sensitivities.py`;
  }
  const family = campaign.family === "gapped" ? "v2_gapped" : "v2_continuous";
  const phase = campaign.phase === "takeoff" || campaign.phase === "landing" ? campaign.phase : "cruise";
  return `post/${family}/plot_${phase}_sensitivities.py`;
}

function codeRefForTsangpoCampaign(spec: TsangpoProjectSpec, campaign: TsangpoCampaignSpec): CodeRef | null {
  const sourceRepo = spec.project.source_repo ?? {};
  const repo = (
    typeof sourceRepo.owner === "string" && typeof sourceRepo.name === "string"
      ? { owner: sourceRepo.owner, name: sourceRepo.name }
      : githubRepoFromUrl(sourceRepo.url)
  );
  const commit = typeof sourceRepo.observed_commit === "string" ? sourceRepo.observed_commit : "";
  if (!repo || !commit) return null;

  const relativePath = tsangpoCampaignScriptPath(campaign);
  const repoUrl = sourceRepo.url ?? `https://github.com/${repo.owner}/${repo.name}`;
  return {
    kind: "local_script",
    provider: "github",
    repository: {
      provider: "github",
      owner: repo.owner,
      name: repo.name,
      url: repoUrl,
    },
    commit,
    relative_path: relativePath,
    url: `${repoUrl}/blob/${commit}/${relativePath}`,
    commit_url: `${repoUrl}/tree/${commit}`,
  };
}

function V3CodeRefSummary({ codeRef }: { codeRef: CodeRef }) {
  const repo = `${codeRef.repository?.owner ?? "github"}/${codeRef.repository?.name ?? "repo"}`;
  const path = codeRef.relative_path ?? "source";
  return (
    <section className="v3-code-ref-summary" aria-label="Generating script">
      <div>
        <span>Generating script</span>
        <strong>{repo} / {path}</strong>
        <em>{codeRef.commit?.slice(0, 12) ?? "unknown"}</em>
      </div>
      {codeRef.url ? (
        <a href={codeRef.url} target="_blank" rel="noreferrer">Open GitHub</a>
      ) : null}
    </section>
  );
}

function V3AssetRail({
  assets,
  litAssetIds,
  selection,
  onSelectAsset,
  onPulseToNode,
}: {
  assets: ThreadAsset[];
  litAssetIds: Set<string>;
  selection: ThreadSelectionState;
  onSelectAsset: (assetId: string, nodeId?: string) => void;
  onPulseToNode: (source: HTMLElement | null, nodeId: string, kind?: string) => void;
}) {
  return (
    <aside className="v3-asset-rail" data-testid="v3-asset-rail">
      <div className="v3-rail-head">
        <p className="v3-eyebrow">Downstream claims · same DAG</p>
        <span>{assets.length}</span>
      </div>
      <div className="v3-assets">
        {assets.map((asset) => {
          const lit = litAssetIds.has(asset.id);
          const pinned = selection.primary.kind === "asset" && selection.primary.assetId === asset.id;
          return (
            <button
              key={asset.id}
              className={lit || pinned ? "v3-asset-card lit" : "v3-asset-card"}
              data-node-id={asset.provenanceNodeId}
              onClick={(event) => {
                onSelectAsset(asset.id, asset.provenanceNodeId);
                onPulseToNode(event.currentTarget, asset.provenanceNodeId ?? "", "artifact");
              }}
            >
              <span className="v3-asset-icon">{assetIcon(asset)}</span>
              <span className="v3-asset-main">
                <strong>{asset.label}</strong>
                <em>{sourceLabel(asset.source)} · {asset.sub}</em>
                <span className="v3-asset-refs">
                  {(asset.refs.rows?.length ?? 0).toLocaleString()} rows · {(asset.refs.cells?.length ?? 0).toLocaleString()} cells
                </span>
                {asset.summary && <small>{asset.summary}</small>}
              </span>
              <span className={`v3-health ${asset.health}`}>{asset.health}</span>
            </button>
          );
        })}
      </div>
    </aside>
  );
}

function rowsForPlotBuilder(data: V3WorkbenchData): Record<string, unknown>[] {
  return data.rows.map((row) => {
    const decorated: Record<string, unknown> = { ...(row as Record<string, unknown>) };
    if (row.rowNodeId) decorated._node_id = row.rowNodeId;
    return decorated;
  });
}

function V3PlotDataBridge({
  data,
  filteredRowIds,
  children,
}: {
  data: V3WorkbenchData;
  filteredRowIds: ReadonlySet<string> | null;
  children: ReactNode;
}) {
  const { setFullData, setFilteredData } = usePlotData();
  const fullRows = useMemo(() => rowsForPlotBuilder(data), [data]);
  const filteredRows = useMemo(
    () => filteredRowIds ? fullRows.filter((row) => filteredRowIds.has(String(row.runId))) : fullRows,
    [filteredRowIds, fullRows],
  );

  useEffect(() => {
    setFullData(fullRows);
    setFilteredData(filteredRows);
  }, [filteredRows, fullRows, setFilteredData, setFullData]);

  return <>{children}</>;
}

function V3PlotStudioRail({
  projectId,
  data,
  filteredRowIds,
}: {
  projectId: string;
  data: V3WorkbenchData;
  filteredRowIds: ReadonlySet<string> | null;
}) {
  if (!UUID_LIKE.test(projectId)) {
    return (
      <section className="v3-saved-plots-rail" data-testid="v3-saved-plots-panel">
        <div className="v3-saved-plots-empty">
          <strong>Plot studio</strong>
          <span>Preparing the Thread project that owns the plot artifacts.</span>
        </div>
      </section>
    );
  }
  return (
    <section className="v3-saved-plots-rail v3-plot-studio" data-testid="v3-saved-plots-panel">
      <ProvenanceProvider projectId={projectId}>
        <PlotDataProvider>
          <V3PlotDataBridge data={data} filteredRowIds={filteredRowIds}>
            <V3PlotStudioContent projectId={projectId} branch={data.branch} />
          </V3PlotDataBridge>
        </PlotDataProvider>
      </ProvenanceProvider>
    </section>
  );
}

function V3PlotStudioContent({ projectId, branch }: { projectId: string; branch: string }) {
  const [tab, setTab] = useState<"saved" | "builder">("saved");
  const [loadedPlotConfig, setLoadedPlotConfig] = useState<PlotBuilderConfig | undefined>();

  const handleSavePlot = useCallback(
    async (
      config: PlotBuilderConfig,
      data: Record<string, unknown>[],
      _thumbnail?: string,
      scope?: PlotScope,
    ) => {
      const sourceNodeIds = [
        ...new Set(
          data
            .map((row) => row._node_id)
            .filter((nodeId): nodeId is string => typeof nodeId === "string" && nodeId.length > 0),
        ),
      ];

      await createPlot({
        project_id: projectId,
        title: config.title || "Untitled Plot",
        chart_type: config.chartType,
        config: config as unknown as Record<string, unknown>,
        data_snapshot: data,
        source_node_ids: sourceNodeIds,
        branch,
        scope: scope ?? (branch === "main" ? "project" : "branch"),
      });
      setTab("saved");
    },
    [branch, projectId],
  );

  const handleLoadPlot = useCallback((config: PlotBuilderConfig) => {
    setLoadedPlotConfig(config);
    setTab("builder");
  }, []);

  return (
    <>
      <div className="v3-plot-studio-head">
        <span className="v3-eyebrow">Plot studio</span>
        <div className="v3-plot-studio-tabs" role="tablist" aria-label="Plot studio">
          <button
            type="button"
            className={tab === "saved" ? "active" : ""}
            role="tab"
            aria-selected={tab === "saved"}
            onClick={() => setTab("saved")}
          >
            Saved
          </button>
          <button
            type="button"
            className={tab === "builder" ? "active" : ""}
            role="tab"
            aria-selected={tab === "builder"}
            onClick={() => setTab("builder")}
          >
            Builder
          </button>
        </div>
      </div>
      <div className="v3-plot-studio-body">
        <Suspense fallback={<div className="v3-saved-plots-empty">Loading plot studio...</div>}>
          {tab === "saved" ? (
            <SavedPlotsPanel projectId={projectId} onLoadPlot={handleLoadPlot} />
          ) : (
            <PlotBuilderPanel projectId={projectId} onSave={handleSavePlot} initialConfig={loadedPlotConfig} />
          )}
        </Suspense>
      </div>
    </>
  );
}

function V3TetherOverlay({
  litAssetIds,
  assets,
}: {
  litAssetIds: Set<string>;
  assets: readonly ThreadAsset[];
}) {
  const lit = assets.filter((asset) => litAssetIds.has(asset.id)).slice(0, 6);
  return (
    <svg className="v3-tethers" viewBox="0 0 1440 900" aria-hidden="true">
      {lit.map((asset, index) => {
        const y = 250 + index * 76;
        return (
          <path key={asset.id} d={`M 880 ${y} C 980 ${y - 40}, 1030 ${y + 24}, 1110 ${y}`} />
        );
      })}
    </svg>
  );
}

function V3CompatibilityLegend({ incompatible }: { incompatible: boolean }) {
  return (
    <footer className="v3-legend" data-testid="v3-compatibility-legend">
      <span><i className="selected" /> selected</span>
      <span><i className="connected" /> connected lineage</span>
      <span><i className="compatible" /> compatible bridge</span>
      <span><i className="adjacent" /> adjacent context</span>
      <span className={incompatible ? "warn active" : "warn"}><i className="incompatible" /> incompatible</span>
    </footer>
  );
}

function V3VariantSwitcher({
  active,
  onChange,
}: {
  active: V3VariantId;
  onChange: (variant: V3VariantId) => void;
}) {
  return (
    <nav className="v3-variant-switcher" aria-label="Design variants" data-testid="v3-variant-switcher">
      {V3_VARIANTS.map((variant, index) => (
        <button
          key={variant.id}
          className={variant.id === active ? "active" : ""}
          onClick={() => onChange(variant.id)}
          title={`${variant.label}: ${variant.motion}`}
        >
          <span>{String(index + 1).padStart(2, "0")}</span>
          {variant.short}
        </button>
      ))}
    </nav>
  );
}

type V3LayoutPresetId = "tsangpo-build" | "table-focus" | "dag-review" | "split-review";

const V3_LAYOUT_PRESETS: Array<{ id: V3LayoutPresetId; label: string; requiresBuild?: boolean }> = [
  { id: "tsangpo-build", label: "Build + table", requiresBuild: true },
  { id: "table-focus", label: "Table focus" },
  { id: "dag-review", label: "DAG review" },
  { id: "split-review", label: "Split review" },
];

function V3LayoutPresetPicker({
  hasBuildDock,
  onApply,
}: {
  hasBuildDock: boolean;
  onApply: (preset: V3LayoutPresetId) => void;
}) {
  return (
    <label className="v3-layout-presets">
      <span>Layout</span>
      <select
        aria-label="Layout presets"
        value=""
        onChange={(event) => {
          const next = event.currentTarget.value as V3LayoutPresetId;
          if (next) onApply(next);
        }}
      >
        <option value="">Preset</option>
        {V3_LAYOUT_PRESETS.map((preset) => (
          <option key={preset.id} value={preset.id} disabled={Boolean(preset.requiresBuild && !hasBuildDock)}>
            {preset.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function selectedRowForLayout(data: V3WorkbenchData, selection: ThreadSelectionState): ThreadRow | null {
  const primary = selection.primary;
  if (primary.kind === "cell" || primary.kind === "row") {
    return data.rows.find((row) => row.runId === primary.rowId) ?? null;
  }
  if (primary.kind === "asset") {
    const asset = data.assets.find((item) => item.id === primary.assetId);
    const rowId = asset?.refs.rows?.[0];
    return rowId ? data.rows.find((row) => row.runId === rowId) ?? null : null;
  }
  return data.rows[0] ?? null;
}

function V3ConditionRail({ data, selection }: { data: V3WorkbenchData; selection: ThreadSelectionState }) {
  const row = selectedRowForLayout(data, selection);
  const conditions = data.columns.filter((column) => column.kind === "condition").slice(0, 5);
  return (
    <section className="v3-condition-rail" aria-label="Condition rails">
      <span className="v3-rail-kicker">condition_key rails</span>
      {conditions.map((column) => (
        <span key={column.id} className="v3-condition-chip">
          {column.label}
          <strong>{displayValue(row?.[column.id])}</strong>
        </span>
      ))}
      <span className="v3-rail-note">walk graph · comb rows · preserve lineage</span>
    </section>
  );
}

function V3ClaimStrip({
  data,
  selection,
  litAssetIds,
}: {
  data: V3WorkbenchData;
  selection: ThreadSelectionState;
  litAssetIds: ReadonlySet<string>;
}) {
  const primary = selection.primary;
  const row = selectedRowForLayout(data, selection);
  const selectedColumn = primary.kind === "cell" ? data.columns.find((column) => column.id === primary.colId) : null;
  const litAssets = data.assets.filter((asset) => litAssetIds.has(asset.id)).slice(0, 3);
  const value = selectedColumn && row ? displayValue(row[selectedColumn.id]) : null;
  const claim = selectedColumn && row
    ? `${selectedColumn.label} ${value} from ${row.runId} can spill into chart labels, slide claims, and summaries.`
    : "Select a row or cell to turn one combed graph strand into claims, charts, decks, and conversations.";

  return (
    <section className="v3-claim-strip" data-testid="v3-claim-strip">
      <p className="v3-eyebrow">Cell-to-claim spill</p>
      <strong>{claim}</strong>
      <div className="v3-claim-pills">
        <span>chart label</span>
        <span>slide headline</span>
        <span>smart summary</span>
        {litAssets.map((asset) => <span key={asset.id}>{asset.label}</span>)}
      </div>
    </section>
  );
}

type V3DockId = "dag-minimap" | "flat-minimap" | "tsangpo-build";

type V3DockState = {
  x: number;
  y: number;
  width: number;
  height: number;
  minimized: boolean;
  hidden: boolean;
};

const DEFAULT_DOCKS: Record<V3DockId, V3DockState> = {
  "dag-minimap": { x: 24, y: 68, width: 440, height: 286, minimized: false, hidden: false },
  "flat-minimap": { x: 24, y: 68, width: 390, height: 420, minimized: false, hidden: false },
  "tsangpo-build": { x: 10, y: 8, width: 304, height: 600, minimized: false, hidden: false },
};

function dockStorageKey(projectId: string, branch: string): string {
  return `thread:v3:docks:v2:${projectId}:${branch}`;
}

function clampDockState(state: V3DockState): V3DockState {
  const viewportWidth = typeof window === "undefined" ? 1440 : window.innerWidth;
  const viewportHeight = typeof window === "undefined" ? 900 : window.innerHeight;
  const width = clampNumber(state.width, 260, Math.max(300, viewportWidth - 24));
  const height = state.minimized ? 44 : clampNumber(state.height, 170, Math.max(220, viewportHeight - 90));
  return {
    ...state,
    width,
    height,
    x: clampNumber(state.x, 8, Math.max(8, viewportWidth - width - 8)),
    y: clampNumber(state.y, 8, Math.max(8, viewportHeight - height - 38)),
  };
}

function compactDagDockState(): Partial<V3DockState> {
  const viewportWidth = typeof window === "undefined" ? 1440 : window.innerWidth;
  const viewportHeight = typeof window === "undefined" ? 900 : window.innerHeight;
  const width = DEFAULT_DOCKS["dag-minimap"].width;
  const height = DEFAULT_DOCKS["dag-minimap"].height;
  return {
    ...DEFAULT_DOCKS["dag-minimap"],
    x: Math.max(8, viewportWidth - width - 18),
    y: Math.max(56, viewportHeight - height - 50),
    hidden: false,
    minimized: false,
  };
}

function expandedDagDockState(): Partial<V3DockState> {
  const viewportWidth = typeof window === "undefined" ? 1440 : window.innerWidth;
  const viewportHeight = typeof window === "undefined" ? 900 : window.innerHeight;
  const sideRailWidth = viewportWidth >= 1120 ? 430 : 24;
  return {
    x: sideRailWidth,
    y: 60,
    width: Math.max(360, viewportWidth - sideRailWidth - 24),
    height: Math.max(280, viewportHeight - 116),
    hidden: false,
    minimized: false,
  };
}

function flatMinimapDockState(): Partial<V3DockState> {
  return { ...DEFAULT_DOCKS["flat-minimap"], hidden: false, minimized: false };
}

function tsangpoBuildDockState(): Partial<V3DockState> {
  const viewportHeight = typeof window === "undefined" ? 900 : window.innerHeight;
  return {
    ...DEFAULT_DOCKS["tsangpo-build"],
    height: Math.max(430, viewportHeight - 118),
    hidden: false,
    minimized: false,
  };
}

function loadDockStates(projectId: string, branch: string): Record<V3DockId, V3DockState> {
  const fallback = {
    "dag-minimap": clampDockState({ ...DEFAULT_DOCKS["dag-minimap"], ...compactDagDockState() }),
    "flat-minimap": clampDockState(DEFAULT_DOCKS["flat-minimap"]),
    "tsangpo-build": clampDockState({ ...DEFAULT_DOCKS["tsangpo-build"], ...tsangpoBuildDockState() }),
  };
  if (typeof window === "undefined") return fallback;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(dockStorageKey(projectId, branch)) ?? "{}") as Partial<Record<V3DockId, Partial<V3DockState>>>;
    return {
      "dag-minimap": clampDockState({ ...fallback["dag-minimap"], ...parsed["dag-minimap"] }),
      "flat-minimap": clampDockState({ ...fallback["flat-minimap"], ...parsed["flat-minimap"] }),
      "tsangpo-build": clampDockState({ ...fallback["tsangpo-build"], ...parsed["tsangpo-build"] }),
    };
  } catch {
    return fallback;
  }
}

function useV3DockStates(projectId: string, branch: string) {
  const storageKey = dockStorageKey(projectId, branch);
  const [docks, setDocks] = useState<Record<V3DockId, V3DockState>>(() => loadDockStates(projectId, branch));

  useEffect(() => {
    setDocks(loadDockStates(projectId, branch));
  }, [projectId, branch]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(storageKey, JSON.stringify(docks));
  }, [docks, storageKey]);

  const updateDock = useCallback((id: V3DockId, patch: Partial<V3DockState> | ((dock: V3DockState) => V3DockState)) => {
    setDocks((current) => {
      const existing = current[id] ?? DEFAULT_DOCKS[id];
      const next = typeof patch === "function" ? patch(existing) : { ...existing, ...patch };
      return { ...current, [id]: clampDockState(next) };
    });
  }, []);

  const resetDock = useCallback((id: V3DockId) => {
    const fallback = id === "dag-minimap"
      ? { ...DEFAULT_DOCKS[id], ...compactDagDockState() }
      : id === "tsangpo-build"
        ? { ...DEFAULT_DOCKS[id], ...tsangpoBuildDockState() }
        : DEFAULT_DOCKS[id];
    setDocks((current) => ({ ...current, [id]: clampDockState(fallback) }));
  }, []);

  const showDock = useCallback((id: V3DockId) => updateDock(id, { hidden: false, minimized: false }), [updateDock]);
  const hideDock = useCallback((id: V3DockId) => updateDock(id, { hidden: true }), [updateDock]);

  return { docks, updateDock, resetDock, showDock, hideDock };
}

function dagLayoutStorageKey(projectId: string, branch: string): string {
  return `thread:v3:dag-layout:v1:${projectId}:${branch}`;
}

function normalizePinnedPosition(position: V3DagPinnedPosition): V3DagPinnedPosition {
  return {
    x: Number(position.x.toFixed(2)),
    y: Number(position.y.toFixed(2)),
    z: typeof position.z === "number" ? Number(position.z.toFixed(3)) : undefined,
  };
}

function sanitizeDagLayout(value: Partial<V3DagLayoutState> | null | undefined): V3DagLayoutState {
  const projectionMode = value?.projectionMode === "plan" ? "plan" : "depth";
  const nodePins = Object.fromEntries(Object.entries(value?.nodePins ?? {})
    .filter(([, position]) => Number.isFinite(position?.x) && Number.isFinite(position?.y))
    .map(([id, position]) => [id, normalizePinnedPosition(position)]));
  const clusterPins = Object.fromEntries(Object.entries(value?.clusterPins ?? {})
    .filter(([, position]) => Number.isFinite(position?.x) && Number.isFinite(position?.y))
    .map(([id, position]) => [id, normalizePinnedPosition(position)]));
  const memberships = Object.fromEntries(Object.entries(value?.memberships ?? {})
    .map(([id, clusters]): [string, string[]] => [id, [...new Set((clusters ?? []).filter(Boolean))].slice(0, 5)])
    .filter(([, clusters]) => clusters.length > 0));

  return {
    projectionMode,
    depthStrength: clampNumber(Number(value?.depthStrength ?? DEFAULT_V3_DAG_LAYOUT.depthStrength), 0, 1.25),
    perspective: clampNumber(Number(value?.perspective ?? DEFAULT_V3_DAG_LAYOUT.perspective), 0, 0.8),
    nodePins,
    clusterPins,
    memberships,
  };
}

function loadDagLayoutState(projectId: string, branch: string): V3DagLayoutState {
  if (typeof window === "undefined") return DEFAULT_V3_DAG_LAYOUT;
  try {
    return sanitizeDagLayout(JSON.parse(window.localStorage.getItem(dagLayoutStorageKey(projectId, branch)) ?? "{}") as Partial<V3DagLayoutState>);
  } catch {
    return DEFAULT_V3_DAG_LAYOUT;
  }
}

function useV3DagLayoutState(projectId: string, branch: string) {
  const storageKey = dagLayoutStorageKey(projectId, branch);
  const [dagLayout, setDagLayout] = useState<V3DagLayoutState>(() => loadDagLayoutState(projectId, branch));

  useEffect(() => {
    setDagLayout(loadDagLayoutState(projectId, branch));
  }, [projectId, branch]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(storageKey, JSON.stringify(dagLayout));
  }, [dagLayout, storageKey]);

  const updateDagLayout = useCallback((patch: Partial<V3DagLayoutState> | ((layout: V3DagLayoutState) => V3DagLayoutState)) => {
    setDagLayout((current) => sanitizeDagLayout(typeof patch === "function" ? patch(current) : { ...current, ...patch }));
  }, []);

  const resetDagLayout = useCallback(() => setDagLayout(DEFAULT_V3_DAG_LAYOUT), []);

  return { dagLayout, updateDagLayout, resetDagLayout };
}

function nearestMembershipCluster(
  data: V3WorkbenchData,
  layout: V3DagLayoutState,
  nodeId: string,
  position: V3DagPinnedPosition,
): string | null {
  const node = data.provenance.nodesById.get(nodeId);
  if (!node) return null;
  let best: { id: string; distance: number } | null = null;
  for (const cluster of data.provenance.clusters) {
    if (cluster.id === node.cluster) continue;
    const projected = v3DagProjectedClusterPosition(cluster, layout);
    const distance = Math.hypot(projected.x - position.x, projected.y - position.y);
    if (distance > cluster.radius * 1.18) continue;
    if (!best || distance < best.distance) best = { id: cluster.id, distance };
  }
  return best?.id ?? null;
}

function V3FloatingDock({
  id,
  title,
  state,
  onChange,
  onHide,
  onReset,
  children,
}: {
  id: V3DockId;
  title: string;
  state: V3DockState;
  onChange: (patch: Partial<V3DockState> | ((dock: V3DockState) => V3DockState)) => void;
  onHide: () => void;
  onReset: () => void;
  children: ReactNode;
}) {
  const dragRef = useRef<{ x: number; y: number; start: V3DockState; mode: "move" | "resize" } | null>(null);
  const [dragging, setDragging] = useState(false);
  if (state.hidden) return null;

  const startDrag = (event: ReactPointerEvent<HTMLElement>, mode: "move" | "resize") => {
    if ((event.target as HTMLElement).closest("button")) return;
    event.preventDefault();
    setDragging(true);
    dragRef.current = { x: event.clientX, y: event.clientY, start: state, mode };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const updateDrag = (event: ReactPointerEvent<HTMLElement>) => {
    const active = dragRef.current;
    if (!active) return;
    const dx = event.clientX - active.x;
    const dy = event.clientY - active.y;
    if (active.mode === "move") {
      onChange({ x: active.start.x + dx, y: active.start.y + dy });
    } else {
      onChange({ width: active.start.width + dx, height: active.start.height + dy });
    }
  };
  const stopDrag = (event: ReactPointerEvent<HTMLElement>) => {
    dragRef.current = null;
    setDragging(false);
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // Capture may already be released by the browser.
    }
  };

  return (
    <aside
      className={[
        "v3-floating-dock",
        `dock-${id}`,
        state.minimized ? "is-minimized" : "",
        dragging ? "is-dragging" : "",
      ].filter(Boolean).join(" ")}
      data-testid={`v3-floating-dock-${id}`}
      style={{ left: state.x, top: state.y, width: state.width, height: state.minimized ? 44 : state.height } as CSSProperties}
    >
      <header
        className="v3-floating-dock-head"
        onPointerDown={(event) => startDrag(event, "move")}
        onPointerMove={updateDrag}
        onPointerUp={stopDrag}
        onPointerCancel={stopDrag}
      >
        <strong>{title}</strong>
        <div>
          <button type="button" data-testid={`v3-dock-minimize-${id}`} onClick={() => onChange((dock) => ({ ...dock, minimized: !dock.minimized }))}>
            {state.minimized ? "Expand" : "Minimize"}
          </button>
          <button type="button" data-testid={`v3-dock-reset-${id}`} onClick={onReset}>Reset</button>
          <button type="button" data-testid={`v3-dock-hide-${id}`} onClick={onHide}>Hide</button>
        </div>
      </header>
      {!state.minimized && <div className="v3-floating-dock-body">{children}</div>}
      {!state.minimized && (
        <span
          className="v3-floating-dock-resize"
          data-testid={`v3-dock-resize-${id}`}
          onPointerDown={(event) => startDrag(event, "resize")}
          onPointerMove={updateDrag}
          onPointerUp={stopDrag}
          onPointerCancel={stopDrag}
        />
      )}
    </aside>
  );
}

function V3FlatProjectionMinimap({
  data,
  columns,
  selection,
  litRowIds,
  activePivot,
  onSelectionChange,
  onOpenFlat,
}: {
  data: V3WorkbenchData;
  columns: readonly ThreadColumn[];
  selection: ThreadSelectionState;
  litRowIds: ReadonlySet<string>;
  activePivot: V3PivotView | null;
  onSelectionChange: (selection: ThreadSelectionState) => void;
  onOpenFlat: () => void;
}) {
  const compatible = new Set(activePivot?.compatibleRowIds ?? []);
  const rows = (activePivot?.compatibleRowIds.length
    ? activePivot.compatibleRowIds.map((rowId) => data.rows.find((row) => row.runId === rowId)).filter((row): row is ThreadRow => Boolean(row))
    : data.rows).slice(0, 36);
  const miniColumns = columns.slice(0, 8);

  return (
    <section className="v3-flat-minimap" data-testid="v3-flat-minimap">
      <div className="v3-flat-minimap-head">
        <div>
          <span>Flat projection</span>
          <strong>{activePivot?.label ?? "No active pivot"}</strong>
        </div>
        <button type="button" onClick={onOpenFlat}>Open Table</button>
      </div>
      <div className="v3-flat-minimap-grid" style={{ gridTemplateColumns: `92px repeat(${miniColumns.length}, minmax(34px, 1fr))` }}>
        <span className="v3-flat-mini-corner">row</span>
        {miniColumns.map((column) => <span key={column.id} className="v3-flat-mini-head">{column.label}</span>)}
        {rows.map((row) => {
          const selected = (selection.primary.kind === "row" || selection.primary.kind === "cell") && selection.primary.rowId === row.runId;
          const lit = litRowIds.has(row.runId);
          const incompatible = activePivot && !compatible.has(row.runId);
          return (
            <div
              key={row.runId}
              className={[
                "v3-flat-mini-row",
                selected ? "is-selected" : "",
                lit ? "is-lit" : "",
                incompatible ? "is-incompatible" : "",
              ].filter(Boolean).join(" ")}
            >
              <button type="button" className="v3-flat-mini-row-label" onClick={() => onSelectionChange({ primary: { kind: "row", rowId: row.runId, nodeId: row.rowNodeId } })}>
                {row.runId}
              </button>
              {miniColumns.map((column) => {
                const handleId = row.cellNodeIds?.[column.id];
                const selectedCell = selection.primary.kind === "cell" && selection.primary.rowId === row.runId && selection.primary.colId === column.id;
                return (
                  <button
                    key={column.id}
                    type="button"
                    className={selectedCell ? "v3-flat-mini-cell is-selected" : "v3-flat-mini-cell"}
                    data-node-id={handleId}
                    onClick={() => onSelectionChange({ primary: { kind: "cell", rowId: row.runId, colId: column.id, nodeId: handleId } })}
                  >
                    {displayValue(row[column.id])}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function V3VariantLayout({
  variantId,
  data,
  selection,
  projectionMode,
  litClusterIds,
  litNodeIds,
  selectedNodeId,
  filteredNodeIds,
  filteredClusterIds,
  filterActive,
  filteredRowIds,
  filteredRowCount,
  litRowIds,
  litAssetIds,
  sliceActive,
  variant,
  surfaceMode,
  projectionDirection,
  dagWidgetVisible,
  assetsVisible,
  docks,
  activePivot,
  dagLayout,
  deckViewState,
  onFocusChange,
  onProjectionChange,
  onSelectionChange,
  onPulseToNode,
  onFilterRowsChange,
  onSelectDagNode,
  onComb,
  onToggleDagWidget,
  onToggleAssets,
  onDockChange,
  onDockHide,
  onDockReset,
  onOpenFlat,
  onDeckViewStateChange,
  onDagLayoutChange,
  onDagLayoutReset,
  onMoveDagNode,
  onDropDagNode,
  onMoveDagCluster,
  onDeepInspect,
  codeSourceProjectId,
}: {
  variantId: V3VariantId;
  data: V3WorkbenchData;
  selection: ThreadSelectionState;
  projectionMode: ProjectionMode;
  litClusterIds: Set<string>;
  litNodeIds: ReadonlySet<string>;
  selectedNodeId: string | null;
  filteredNodeIds: ReadonlySet<string> | null;
  filteredClusterIds: ReadonlySet<string>;
  filterActive: boolean;
  filteredRowIds: ReadonlySet<string> | null;
  filteredRowCount: number;
  litRowIds: ReadonlySet<string>;
  litAssetIds: Set<string>;
  sliceActive: boolean;
  variant: V3Variant;
  surfaceMode: V3SurfaceMode;
  projectionDirection: V3ProjectionDirection;
  dagWidgetVisible: boolean;
  assetsVisible: boolean;
  docks: Record<V3DockId, V3DockState>;
  activePivot: V3PivotView | null;
  dagLayout: V3DagLayoutState;
  deckViewState: V3DeckViewState;
  onFocusChange: (focused: boolean) => void;
  onProjectionChange: (projection: ProjectionMode) => void;
  onSelectionChange: (selection: ThreadSelectionState) => void;
  onPulseToNode: (source: HTMLElement | null, nodeId: string, kind?: string) => void;
  onFilterRowsChange: (rowIds: Set<string> | null) => void;
  onSelectDagNode: (nodeId: string) => void;
  onComb: () => void;
  onToggleDagWidget: () => void;
  onToggleAssets: () => void;
  onDockChange: (id: V3DockId, patch: Partial<V3DockState> | ((dock: V3DockState) => V3DockState)) => void;
  onDockHide: (id: V3DockId) => void;
  onDockReset: (id: V3DockId) => void;
  onOpenFlat: () => void;
  onDeckViewStateChange: (viewState: V3DeckViewState) => void;
  onDagLayoutChange: (patch: Partial<V3DagLayoutState> | ((layout: V3DagLayoutState) => V3DagLayoutState)) => void;
  onDagLayoutReset: () => void;
  onMoveDagNode: (nodeId: string, position: V3DagPinnedPosition) => void;
  onDropDagNode: (nodeId: string, position: V3DagPinnedPosition) => void;
  onMoveDagCluster: (clusterId: string, position: V3DagPinnedPosition) => void;
  onDeepInspect: () => void;
  codeSourceProjectId: string;
}) {
  const tsangpo = useTsangpoSpec(isTsangpoProject(data));
  const topology = (
    <V3TopologyLayer
      data={data}
      litClusterIds={litClusterIds}
      litNodeIds={litNodeIds}
      selectedNodeId={selectedNodeId}
      filteredNodeIds={filteredNodeIds}
      filteredClusterIds={filteredClusterIds}
      filterActive={filterActive}
      sliceActive={sliceActive}
      variant={variant}
      projectionDirection={projectionDirection}
      dagLayout={dagLayout}
      deckViewState={deckViewState}
      onFocusChange={onFocusChange}
      onSelectNode={onSelectDagNode}
      onComb={onComb}
      onDeckViewStateChange={onDeckViewStateChange}
      onDagLayoutChange={onDagLayoutChange}
      onDagLayoutReset={onDagLayoutReset}
      onMoveDagNode={onMoveDagNode}
      onDropDagNode={onDropDagNode}
      onMoveDagCluster={onMoveDagCluster}
      onDeepInspect={onDeepInspect}
    />
  );
  const projection = (
    <V3ProjectionStage
      data={data}
      projection={projectionMode}
      selection={selection}
      litRowIds={litRowIds}
      filteredRowIds={filteredRowIds}
      filteredRowCount={filteredRowCount}
      filterActive={filterActive}
      onProjectionChange={onProjectionChange}
      onSelectionChange={onSelectionChange}
      onPulseToNode={onPulseToNode}
      onFilterRowsChange={onFilterRowsChange}
    />
  );
  const assets = (
    <>
      {tsangpo.spec && <V3PlotStudioRail projectId={codeSourceProjectId} data={data} filteredRowIds={filteredRowIds} />}
      <V3AssetRail
        assets={data.assets}
        litAssetIds={litAssetIds}
        selection={selection}
        onSelectAsset={(assetId, nodeId) => onSelectionChange({ primary: { kind: "asset", assetId, nodeId } })}
        onPulseToNode={onPulseToNode}
      />
    </>
  );
  const rail = <V3ConditionRail data={data} selection={selection} />;
  const claims = <V3ClaimStrip data={data} selection={selection} litAssetIds={litAssetIds} />;
  const selectedRow = selectedRowForLayout(data, selection);
  const selectedFamily = selectedRow?.family ? data.families[selectedRow.family]?.label ?? selectedRow.family : "mixed";
  const tsangpoBuildDock = docks["tsangpo-build"];
  const tsangpoBuildReserve = tsangpo.spec && !tsangpoBuildDock.hidden && !tsangpoBuildDock.minimized && tsangpoBuildDock.x <= 96 && surfaceMode !== "dag"
    ? `${Math.min(tsangpoBuildDock.x + tsangpoBuildDock.width + 12, 440)}px`
    : "0px";
  const v4Badge = (label: string, parents: string) => (
    <div className="v4-candidate-badge" data-testid="v4-candidate-badge">
      <strong>{label}</strong>
      <span>{parents}</span>
    </div>
  );

  const shell = (children: ReactNode) => (
    <div
      className={[
        "v3-layout",
        "v3-live-layout",
        `layout-${variantId}`,
        tsangpo.spec ? "has-tsangpo-build" : "",
        dagWidgetVisible ? "dag-widget-visible" : "dag-widget-hidden",
        assetsVisible ? "assets-visible" : "assets-hidden",
        surfaceMode !== "table" ? "dag-expanded" : "dag-widget-compact",
        `surface-${surfaceMode}`,
      ].join(" ")}
      data-testid="v3-variant-layout"
      data-layout={variantId}
      style={{ "--v3-build-dock-reserve": tsangpoBuildReserve } as CSSProperties}
    >
      {children}
    </div>
  );

  switch (variantId) {
    case "redux-provenance":
      return shell(
        <>
          {(dagWidgetVisible || surfaceMode !== "table") && (
            <V3FloatingDock
              id="dag-minimap"
              title="DAG minimap"
              state={docks["dag-minimap"]}
              onChange={(patch) => onDockChange("dag-minimap", patch)}
              onHide={() => {
                onDockHide("dag-minimap");
                onToggleDagWidget();
              }}
              onReset={() => onDockReset("dag-minimap")}
            >
              <div className="v3-dock-topology" data-testid="v3-dag-widget">
                {topology}
              </div>
            </V3FloatingDock>
          )}
          {surfaceMode === "dag" && (
            <V3FloatingDock
              id="flat-minimap"
              title="Flat minimap"
              state={docks["flat-minimap"]}
              onChange={(patch) => onDockChange("flat-minimap", patch)}
              onHide={() => onDockHide("flat-minimap")}
              onReset={() => onDockReset("flat-minimap")}
            >
              <V3FlatProjectionMinimap
                data={data}
                columns={data.columns}
                selection={selection}
                litRowIds={litRowIds}
                activePivot={activePivot}
                onSelectionChange={onSelectionChange}
                onOpenFlat={onOpenFlat}
              />
            </V3FloatingDock>
          )}
          {tsangpo.spec && (
            <V3FloatingDock
              id="tsangpo-build"
              title="Tsangpo build"
              state={docks["tsangpo-build"]}
              onChange={(patch) => onDockChange("tsangpo-build", patch)}
              onHide={() => onDockHide("tsangpo-build")}
              onReset={() => onDockReset("tsangpo-build")}
            >
              <V3TsangpoBuildPanel spec={tsangpo.spec} source={tsangpo.source} projectId={codeSourceProjectId} />
            </V3FloatingDock>
          )}
          <section
            className="v3-redux-stage"
            data-testid="v3-redux-stage"
            data-projection-hidden={surfaceMode === "dag" ? "true" : "false"}
            aria-hidden={surfaceMode === "dag" ? true : undefined}
          >
            {rail}
            {projection}
          </section>
          {assetsVisible && (
            <aside className="v3-redux-assets" data-testid="v3-redux-assets">
              <button type="button" className="v3-assets-collapse" onClick={onToggleAssets}>
                Hide artifacts
              </button>
              {claims}
              {assets}
            </aside>
          )}
        </>,
      );
    case "graph-explorer-canvas":
      return shell(
        <>
          <aside className="v3-graph-canvas">
            {topology}
            {rail}
          </aside>
          <section className="v3-explorer-projection">
            {claims}
            {projection}
          </section>
          <aside className="v3-explorer-inspector">
            <div className="v3-layout-note">Selected slice · {selectedFamily}</div>
            {assets}
          </aside>
        </>,
      );
    case "neo4j-editor-darkroom":
      return shell(
        <>
          <div className="v3-query-strip">
            <span>MATCH</span>
            <code>(condition)-[:COMBS]-&gt;(row)-[:FEEDS]-&gt;(asset)</code>
            <strong>{selectedRow?.runId ?? "no row"}</strong>
          </div>
          <section className="v3-editor-canvas">
            {topology}
            {claims}
          </section>
          <aside className="v3-node-inspector">
            {rail}
            {assets}
          </aside>
          <section className="v3-result-dock">{projection}</section>
        </>,
      );
    case "drug-discovery-matrix":
      return shell(
        <>
          <div className="v3-matrix-topology">{topology}</div>
          {rail}
          <aside className="v3-matrix-lanes">
            <span>source fidelity</span>
            <span>model family</span>
            <span>compatibility bridge</span>
            <span>review state</span>
            <span>downstream claim</span>
          </aside>
          <section className="v3-matrix-projection">{projection}</section>
          <aside className="v3-matrix-assets">
            {claims}
            {assets}
          </aside>
        </>,
      );
    case "instinct-orbital":
      return shell(
        <>
          <div className="v3-orbit-field">{topology}</div>
          <section className="v3-orbit-core">{projection}</section>
          <aside className="v3-orbit-assets">{assets}</aside>
          <div className="v3-orbit-claims">{claims}</div>
        </>,
      );
    case "loom-weave-projection":
      return shell(
        <>
          <section className="v3-loom-weave">
            <div className="v3-loom-source">{topology}</div>
            {rail}
            <div className="v3-loom-table">{projection}</div>
            <div className="v3-loom-claims">
              {claims}
              {assets}
            </div>
          </section>
        </>,
      );
    case "xray-table":
      return shell(
        <>
          <div className="v3-xray-underlay">{topology}</div>
          <section className="v3-xray-projection">{projection}</section>
          <aside className="v3-xray-assets">{assets}</aside>
          <div className="v3-xray-claims">{claims}</div>
        </>,
      );
    case "correlogram-constellation":
      return shell(
        <>
          <section className="v3-constellation-sky">
            {topology}
            {rail}
          </section>
          <section className="v3-constellation-projection">{projection}</section>
          <aside className="v3-constellation-assets">
            {claims}
            {assets}
          </aside>
        </>,
      );
    case "asset-gravity-well":
      return shell(
        <>
          <section className="v3-gravity-table">{projection}</section>
          <aside className="v3-gravity-assets">{assets}</aside>
          <div className="v3-gravity-topology">{topology}</div>
          <div className="v3-gravity-claims">{claims}</div>
        </>,
      );
    case "presentation-command-deck":
      return shell(
        <>
          <section className="v3-command-slide">
            <div className="v3-command-topology">{topology}</div>
            <p className="v3-eyebrow">Slide claim preview</p>
            {claims}
            <div className="v3-slide-mock">
              <strong>{selectedRow?.runId ?? "Selected evidence"} drives this claim</strong>
              <span>Chart label · slide title · provenance footer all point back to the same row/cell strand.</span>
            </div>
          </section>
          <section className="v3-command-source">{projection}</section>
          <aside className="v3-command-assets">
            {rail}
            {assets}
          </aside>
        </>,
      );
    case "v4-xray-ribbon":
      return shell(
        <>
          <div className="v4-xray-topology">{topology}</div>
          {rail}
          <section className="v4-xray-workbench">
            {v4Badge("V4 X-Ray Ribbon", "x-ray table + cluster ribbon + asset gravity")}
            <div className="v4-xray-stage">{projection}</div>
            <div className="v4-xray-claim">{claims}</div>
          </section>
          <aside className="v4-xray-assets">{assets}</aside>
        </>,
      );
    case "v4-loom-ribbon":
      return shell(
        <>
          <div className="v4-loom-topology">{topology}</div>
          <section className="v4-loom-comb">
            {v4Badge("V4 Loom Ribbon", "cluster ribbon + comb/weave + asset tethers")}
            <div className="v4-strand-strip" aria-hidden="true">
              <span>messy DAG</span>
              <i />
              <span>condition rails</span>
              <i />
              <span>AG Grid table</span>
              <i />
              <span>claims</span>
            </div>
            {rail}
          </section>
          <section className="v4-loom-projection">{projection}</section>
          <aside className="v4-loom-assets">
            {claims}
            {assets}
          </aside>
        </>,
      );
    case "v4-matrix-bridge":
      return shell(
        <>
          <div className="v4-bridge-topology">{topology}</div>
          <section className="v4-bridge-rails">
            {v4Badge("V4 Matrix Bridge", "matrix bands + x-ray lineage + compatibility lanes")}
            {rail}
            <div className="v4-bridge-lanes" aria-label="Compatibility bridge lanes">
              <span className="connected">connected lineage</span>
              <span className="compatible">compatible bridge</span>
              <span className="adjacent">adjacent context</span>
              <span className="incompatible">not comparable</span>
            </div>
          </section>
          <aside className="v4-bridge-sideband" aria-label="Lineage matrix bands">
            <span>source fidelity</span>
            <span>model family</span>
            <span>condition rail</span>
            <span>compatibility rule</span>
            <span>review state</span>
            <span>downstream claim</span>
          </aside>
          <section className="v4-bridge-projection">{projection}</section>
          <aside className="v4-bridge-assets">
            {claims}
            {assets}
          </aside>
        </>,
      );
    case "cluster-ribbon-pro":
    default:
      return shell(
        <>
          <div className="v3-layout-topology">{topology}</div>
          {rail}
          <div className="v3-layout-main">
            {projection}
            {assets}
          </div>
        </>,
      );
  }
}

function hasIncompatible(data: V3WorkbenchData, selection: ThreadSelectionState): boolean {
  if (selection.primary.kind !== "cell") return false;
  const primary = selection.primary;
  const selectedRow = data.rows.find((row) => row.runId === primary.rowId);
  const selectedCol = data.columns.find((column) => column.id === primary.colId);
  if (!selectedRow || !selectedCol) return false;
  return data.rows.some((row) => classifyCompatibility({
    row,
    column: selectedCol,
    selectedRow,
    selection,
    bridges: data.bridges,
  }) === "incompatible");
}

type V3Particle = {
  id: string;
  nodeId: string;
  from: { x: number; y: number };
  to: { x: number; y: number };
  kind: string;
  delayMs: number;
  durationMs: number;
  label?: string;
};

type V3GridCellOverlayDirection = "to-dag" | "to-flat";

type V3GridCellOverlayItem = {
  id: string;
  nodeId: string;
  rowId: string;
  rowNodeId: string;
  colId: string;
  text: string;
  direction: V3GridCellOverlayDirection;
  from: { x: number; y: number; width: number; height: number };
  to: { x: number; y: number };
};

type V3DagMode = "hidden" | "ambient" | "focus";

function particleDelayStep(kind: string): number {
  if (kind === "melt") return 4;
  if (kind === "freeze") return 3;
  return 3;
}

function particleDuration(kind: string): number {
  if (kind === "melt") return 980;
  if (kind === "freeze") return 860;
  return 760;
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function interpolateDeckViewState(from: V3DeckViewState, to: V3DeckViewState, t: number): V3DeckViewState {
  return {
    target: [
      from.target[0] + (to.target[0] - from.target[0]) * t,
      from.target[1] + (to.target[1] - from.target[1]) * t,
      from.target[2] + (to.target[2] - from.target[2]) * t,
    ],
    zoom: from.zoom + (to.zoom - from.zoom) * t,
  };
}

function elementCenter(element: HTMLElement | null, root: HTMLElement | null): { x: number; y: number } | null {
  if (!element || !root) return null;
  const rect = element.getBoundingClientRect();
  const rootRect = root.getBoundingClientRect();
  return {
    x: rect.left + rect.width / 2 - rootRect.left,
    y: rect.top + rect.height / 2 - rootRect.top,
  };
}

function elementRectInRoot(element: HTMLElement, root: HTMLElement): { x: number; y: number; width: number; height: number } {
  const rect = element.getBoundingClientRect();
  const rootRect = root.getBoundingClientRect();
  return {
    x: rect.left - rootRect.left,
    y: rect.top - rootRect.top,
    width: rect.width,
    height: rect.height,
  };
}

function cssEscape(value: string): string {
  return globalThis.CSS?.escape ? globalThis.CSS.escape(value) : value.replace(/["\\]/g, "\\$&");
}

function graphNodeScreenPosition(
  nodeId: string,
  data: V3WorkbenchData,
  root: HTMLElement | null,
  deckViewState?: V3DeckViewState,
  dagLayout: V3DagLayoutState = DEFAULT_V3_DAG_LAYOUT,
): { x: number; y: number } | null {
  if (!root) return null;
  const node = data.provenance.nodesById.get(nodeId);
  const deckLayer =
    root.querySelector<HTMLElement>('[data-testid="v3-floating-dock-dag-minimap"] .v3-deck-dag-ready') ??
    root.querySelector<HTMLElement>('[data-testid="v3-floating-dock-dag-minimap"] .v3-deck-dag') ??
    root.querySelector<HTMLElement>(".v3-deck-dag-ready") ??
    root.querySelector<HTMLElement>(".v3-deck-dag");
  if (node && deckLayer && deckViewState) {
    const rect = deckLayer.getBoundingClientRect();
    const rootRect = root.getBoundingClientRect();
    const scale = 2 ** deckViewState.zoom;
    const cssScaleX = rect.width / Math.max(deckLayer.offsetWidth, 1);
    const cssScaleY = rect.height / Math.max(deckLayer.offsetHeight, 1);
    const projected = v3DagProjectedNodePosition(node, data.provenance, dagLayout);
    return {
      x: rect.left - rootRect.left + rect.width / 2 + (projected.x - deckViewState.target[0]) * scale * cssScaleX,
      y: rect.top - rootRect.top + rect.height / 2 + (projected.y - deckViewState.target[1]) * scale * cssScaleY,
    };
  }
  const target = root.querySelector<SVGGraphicsElement>(`[data-graph-node-id="${cssEscape(nodeId)}"]`);
  if (target) {
    const rect = target.getBoundingClientRect();
    const rootRect = root.getBoundingClientRect();
    return { x: rect.left + rect.width / 2 - rootRect.left, y: rect.top + rect.height / 2 - rootRect.top };
  }
  const svg = root.querySelector<SVGSVGElement>(".v3-provenance-svg");
  if (!node || !svg) return null;
  const rect = svg.getBoundingClientRect();
  const rootRect = root.getBoundingClientRect();
  const viewBox = svg.viewBox.baseVal;
  return {
    x: rect.left - rootRect.left + ((node.position.x - viewBox.x) / viewBox.width) * rect.width,
    y: rect.top - rootRect.top + ((node.position.y - viewBox.y) / viewBox.height) * rect.height,
  };
}

function deckViewStateForNodes(
  nodeIds: Iterable<string>,
  data: V3WorkbenchData,
  root: HTMLElement | null,
  fallback: V3DeckViewState,
  dagLayout: V3DagLayoutState,
  options: { padding?: number; minZoom?: number; maxZoom?: number } = {},
): V3DeckViewState {
  const nodes = [...nodeIds]
    .map((id) => data.provenance.nodesById.get(id))
    .filter((node): node is NonNullable<typeof node> => Boolean(node && !isProjectionNodeType(node.type)));
  if (!nodes.length) return fallback;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const node of nodes) {
    const projected = v3DagProjectedNodePosition(node, data.provenance, dagLayout);
    minX = Math.min(minX, projected.x);
    minY = Math.min(minY, projected.y);
    maxX = Math.max(maxX, projected.x);
    maxY = Math.max(maxY, projected.y);
  }

  const deckLayer =
    root?.querySelector<HTMLElement>('[data-testid="v3-floating-dock-dag-minimap"] .v3-deck-dag-ready') ??
    root?.querySelector<HTMLElement>('[data-testid="v3-floating-dock-dag-minimap"] .v3-deck-dag') ??
    root?.querySelector<HTMLElement>(".v3-deck-dag-ready") ??
    root?.querySelector<HTMLElement>(".v3-deck-dag");
  const width = Math.max(deckLayer?.clientWidth ?? 900, 1);
  const height = Math.max(deckLayer?.clientHeight ?? 420, 1);
  const padding = options.padding ?? 1.72;
  const paddedWidth = Math.max(maxX - minX, 130) * padding;
  const paddedHeight = Math.max(maxY - minY, 110) * padding;
  const fitScale = Math.min(width / paddedWidth, height / paddedHeight);

  return {
    target: [
      Number(((minX + maxX) / 2).toFixed(2)),
      Number(((minY + maxY) / 2).toFixed(2)),
      fallback.target[2],
    ],
    zoom: clampNumber(
      Math.log2(Math.max(fitScale, 0.1)),
      options.minZoom ?? -2.6,
      options.maxZoom ?? 2.15,
    ),
  };
}

function V3ParticleOverlay({ particles }: { particles: readonly V3Particle[] }) {
  return (
    <div className="v3-particle-layer" aria-hidden="true">
      {particles.map((particle) => (
        <span
          key={particle.id}
          className={`v3-particle v3-particle-${particle.kind}`}
          data-node-id={particle.nodeId}
          data-kind={particle.kind}
          data-to={`${particle.to.x.toFixed(1)},${particle.to.y.toFixed(1)}`}
          style={{
            left: particle.from.x,
            top: particle.from.y,
            "--dx": `${particle.to.x - particle.from.x}px`,
            "--dy": `${particle.to.y - particle.from.y}px`,
            "--v3-particle-delay": `${particle.delayMs}ms`,
            "--v3-particle-duration": `${particle.durationMs}ms`,
          } as CSSProperties}
        >
          {particle.label && <span>{particle.label}</span>}
        </span>
      ))}
    </div>
  );
}

function V3GridCellOverlay({ cells }: { cells: readonly V3GridCellOverlayItem[] }) {
  if (!cells.length) return null;
  return (
    <div className="v3-cell-overlay-layer" aria-hidden="true" data-testid="v3-cell-overlay">
      {cells.map((cell, index) => (
        <span
          key={cell.id}
          className={`v3-cell-overlay-chip is-${cell.direction}`}
          data-node-id={cell.nodeId}
          data-row-id={cell.rowId}
          data-row-node-id={cell.rowNodeId}
          data-col-id={cell.colId}
          data-direction={cell.direction}
          data-to={`${cell.to.x.toFixed(1)},${cell.to.y.toFixed(1)}`}
          style={{
            left: cell.from.x,
            top: cell.from.y,
            width: Math.max(cell.from.width, 18),
            height: Math.max(cell.from.height, 14),
            "--dx": `${cell.to.x - cell.from.x - cell.from.width / 2}px`,
            "--dy": `${cell.to.y - cell.from.y - cell.from.height / 2}px`,
            "--v3-cell-overlay-delay": `${Math.min(index * 2, 140)}ms`,
            "--v3-cell-overlay-duration": `${cell.direction === "to-flat" ? CELL_OVERLAY_FREEZE_MS : CELL_OVERLAY_MELT_MS}ms`,
          } as CSSProperties}
        >
          {cell.text}
        </span>
      ))}
    </div>
  );
}

function V3DeepGraphModal({
  data,
  onClose,
}: {
  data: V3WorkbenchData;
  onClose: () => void;
}) {
  return (
    <aside className="v3-deep-modal" role="dialog" aria-modal="true" aria-label="Full graph inspection cost">
      <div>
        <strong>Full graph inspection is expensive</strong>
        <p>
          This project represents roughly {data.provenance.representedScale.totalNodes.toLocaleString()} DAG nodes and{" "}
          {data.provenance.representedScale.totalEdges.toLocaleString()} edges. The current projection materializes{" "}
          {data.provenance.nodes.length.toLocaleString()} positioned nodes.
        </p>
        <div className="v3-deep-actions">
          <button type="button" onClick={onClose}>Start clustered</button>
          <button type="button" onClick={onClose}>Load 2-hop neighborhood</button>
          <button type="button" onClick={onClose}>Render full graph anyway</button>
        </div>
      </div>
    </aside>
  );
}

function V3TsangpoBuildPanel({
  spec,
  source,
  projectId,
}: {
  spec: TsangpoProjectSpec;
  source: "api" | "fixture" | "none";
  projectId: string;
}) {
  const [selectedId, setSelectedId] = useState(() => spec.campaigns[0]?.id ?? "");
  const selectedCampaign = spec.campaigns.find((campaign) => campaign.id === selectedId) ?? spec.campaigns[0] ?? null;
  const [baselineDraft, setBaselineDraft] = useState<Record<string, number>>(() => selectedCampaign?.baseline ?? {});
  const [planning, setPlanning] = useState(false);
  const [planMessage, setPlanMessage] = useState("");
  const [buildJobs, setBuildJobs] = useState<TsangpoBuildJob[]>([]);
  const families = useMemo(() => [...new Set(spec.campaigns.map((campaign) => campaign.family))], [spec.campaigns]);
  const phases = useMemo(() => [...new Set(spec.campaigns.map((campaign) => campaign.phase))], [spec.campaigns]);
  const sourceProjectReady = UUID_LIKE.test(projectId);

  useEffect(() => {
    if (!selectedCampaign) return;
    setBaselineDraft(selectedCampaign.baseline);
    setPlanMessage("");
  }, [selectedCampaign]);

  useEffect(() => {
    if (!spec.campaigns.some((campaign) => campaign.id === selectedId)) {
      setSelectedId(spec.campaigns[0]?.id ?? "");
    }
  }, [selectedId, spec.campaigns]);

  useEffect(() => {
    if (!sourceProjectReady) return;
    let cancelled = false;
    const loadJobs = async () => {
      try {
        const response = await fetchTsangpoBuildJobs(projectId);
        if (!cancelled) setBuildJobs(response.jobs ?? []);
      } catch {
        if (!cancelled) setBuildJobs([]);
      }
    };
    void loadJobs();
    const timer = window.setInterval(loadJobs, 30000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [projectId, sourceProjectReady]);

  if (!selectedCampaign) return null;
  const sourceCodeRef = codeRefForTsangpoCampaign(spec, selectedCampaign);
  const selectedJob = buildJobs.find((job) => job.campaign_id === selectedCampaign.id);
  const selectedJobCounts = (selectedJob?.metadata?.case_status_counts ?? {}) as Record<string, number>;
  const selectedJobCaseCount = Number(selectedJob?.metadata?.case_count ?? selectedJob?.flow360_case_ids?.length ?? 0);
  const selectedJobCompleted = Number(selectedJobCounts.completed ?? selectedJobCounts.COMPLETED ?? 0);

  const selectMatchingCampaign = (patch: Partial<Pick<TsangpoCampaignSpec, "family" | "phase">>) => {
    const next = spec.campaigns.find((campaign) => (
      (patch.family ? campaign.family === patch.family : campaign.family === selectedCampaign.family) &&
      (patch.phase ? campaign.phase === patch.phase : campaign.phase === selectedCampaign.phase)
    )) ?? spec.campaigns.find((campaign) => patch.family ? campaign.family === patch.family : campaign.phase === patch.phase);
    if (next) setSelectedId(next.id);
  };

  const sweepCount = Object.values(selectedCampaign.sweeps).reduce((total, values) => total + values.length, 0);
  const localOperationCount = sweepCount + 3;
  const updateBaseline = (axis: string, raw: string) => {
    const value = Number(raw);
    if (!Number.isFinite(value)) return;
    setBaselineDraft((current) => ({ ...current, [axis]: value }));
  };

  const createRunPlan = async () => {
    setPlanning(true);
    setPlanMessage("");
    try {
      const response = await planTsangpoCampaign(
        selectedCampaign.id,
        { baseline: baselineDraft },
        sourceProjectReady ? projectId : undefined,
      );
      const jobSuffix = response.job?.id ? ` · job ${response.job.id.slice(0, 8)}` : "";
      const planVerb = response.status === "queued" ? "queued" : "planned";
      setPlanMessage(`${response.run_plan.operation_count} Thread ops ${planVerb}${jobSuffix}`);
    } catch {
      setPlanMessage(`${localOperationCount} Thread ops staged locally`);
    } finally {
      setPlanning(false);
    }
  };

  return (
    <aside className="v3-tsangpo-build-panel" data-testid="v3-tsangpo-build-panel" aria-label="Tsangpo build panel">
      <header className="v3-tsangpo-build-head">
        <div>
          <span>Build</span>
          <strong>Tsangpo</strong>
        </div>
        <em>{source === "api" ? "API" : "fixture"}</em>
      </header>

      <div className="v3-tsangpo-segments" aria-label="Geometry family">
        {families.map((family) => (
          <button
            key={family}
            type="button"
            className={selectedCampaign.family === family ? "active" : ""}
            onClick={() => selectMatchingCampaign({ family })}
          >
            {family}
          </button>
        ))}
      </div>

      <div className="v3-tsangpo-segments" aria-label="Flight phase">
        {phases.map((phase) => (
          <button
            key={phase}
            type="button"
            className={selectedCampaign.phase === phase ? "active" : ""}
            onClick={() => selectMatchingCampaign({ phase })}
          >
            {phase}
          </button>
        ))}
      </div>

      <label className="v3-tsangpo-picker">
        <span>Campaign</span>
        <select value={selectedCampaign.id} onChange={(event) => setSelectedId(event.target.value)}>
          {spec.campaigns.map((campaign) => (
            <option key={campaign.id} value={campaign.id}>{campaign.label}</option>
          ))}
        </select>
      </label>

      <section className="v3-tsangpo-run-card">
        <div className="v3-tsangpo-run-title">
          <strong>{selectedCampaign.label}</strong>
          <span>{selectedCampaign.planned_case_count} cases</span>
        </div>
        <dl className="v3-tsangpo-baseline">
          {Object.entries(baselineDraft).map(([axis, value]) => (
            <div key={axis}>
              <dt>{axisLabel(axis)}</dt>
              <dd>
                <input
                  type="number"
                  step={axis === "thrust_mult" ? "0.25" : "0.5"}
                  value={value}
                  onChange={(event) => updateBaseline(axis, event.target.value)}
                />
              </dd>
            </div>
          ))}
        </dl>
        <div className="v3-tsangpo-sweeps">
          {Object.entries(selectedCampaign.sweeps).map(([axis, values]) => (
            <span key={axis}>{axisLabel(axis)} {values.length}</span>
          ))}
        </div>
        <div className="v3-tsangpo-lineage">
          <span>{selectedCampaign.geometry_variant_id.replace(/_/g, " ")}</span>
          <code title={selectedCampaign.flow360_project_id}>{selectedCampaign.flow360_project_id.slice(0, 12)}</code>
        </div>
        {selectedJob && (
          <div className="v3-tsangpo-job-state">
            <span>{selectedJob.status.replace(/_/g, " ")}</span>
            <strong>{selectedJobCompleted}/{selectedJobCaseCount || selectedCampaign.planned_case_count} complete</strong>
          </div>
        )}
        <button type="button" className="v3-tsangpo-submit" disabled={planning} onClick={createRunPlan}>
          {planning ? "Planning..." : "Queue in Thread"}
        </button>
        {planMessage && <output className="v3-tsangpo-plan">{planMessage}</output>}
      </section>
      {sourceCodeRef && sourceProjectReady ? (
        <CodeSourcePanel projectId={projectId} codeRef={sourceCodeRef} isLight={false} />
      ) : sourceCodeRef ? (
        <V3CodeRefSummary codeRef={sourceCodeRef} />
      ) : null}
    </aside>
  );
}

export function ThreadV3WorkbenchPage() {
  const { projectId: routeProjectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedProject = routeProjectId ?? searchParams.get("project") ?? undefined;
  const requestedBranch = searchParams.get("branch");
  const inviteParam = searchParams.get("invite");
  const variantId = parseV3VariantId(searchParams.get("variant"));
  const variant = v3VariantById(variantId);
  const load = useV3Data(requestedProject, requestedBranch, searchParams.toString());
  const data = load.data;
  const [projection, setProjectionState] = useState<ProjectionMode>(() => projectionModeFromSearch(searchParams));
  const [selection, setSelection] = useState<ThreadSelectionState>({ primary: { kind: "none" } });
  const [topologyFocus, setTopologyFocus] = useState(false);
  const [dagMode, setDagMode] = useState<V3DagMode>("ambient");
  const [particles, setParticles] = useState<V3Particle[]>([]);
  const [cellOverlay, setCellOverlay] = useState<V3GridCellOverlayItem[]>([]);
  const [deepOpen, setDeepOpen] = useState(false);
  const [combing, setCombing] = useState(false);
  const [surfaceMode, setSurfaceMode] = useState<V3SurfaceMode>("table");
  const [projectionDirection, setProjectionDirection] = useState<V3ProjectionDirection>(null);
  const [projectionProgress, setProjectionProgress] = useState(0);
  const [meltTargetNodeIds, setMeltTargetNodeIds] = useState<Set<string>>(() => new Set());
  const [filteredRowIds, setFilteredRowIds] = useState<Set<string> | null>(null);
  const [projectMenuOpen, setProjectMenuOpen] = useState(false);
  const [projectDraftName, setProjectDraftName] = useState("Untitled Thread project");
  const [membersOpen, setMembersOpen] = useState(false);
  const inviteAcceptAttemptedRef = useRef<string | null>(null);
  const projectDirectory = useV3ProjectDirectory(projectMenuOpen);
  const [deckViewState, setDeckViewState] = useState<V3DeckViewState>({ target: [640, 310, 0], zoom: 0 });
  const [dagWidgetVisible, setDagWidgetVisible] = useState(true);
  const [assetsVisible, setAssetsVisible] = useState(false);
  const layoutProjectId = data.project?.id ?? requestedProject ?? "local-project";
  const hasBuildDock = isTsangpoProject(data);
  const tsangpoThreadProject = useTsangpoThreadProject(hasBuildDock, data.project?.id);
  const membersProjectId = hasBuildDock && data.project?.id && !UUID_LIKE.test(data.project.id)
    ? tsangpoThreadProject?.id ?? data.project.id
    : data.project?.id ?? requestedProject ?? "";
  const membersReady = !hasBuildDock || !data.project?.id || UUID_LIKE.test(data.project.id) || Boolean(tsangpoThreadProject);
  const { docks, updateDock, resetDock, showDock, hideDock } = useV3DockStates(layoutProjectId, data.branch);
  const { dagLayout, updateDagLayout, resetDagLayout } = useV3DagLayoutState(layoutProjectId, data.branch);
  useEffect(() => {
    if (hasBuildDock) setAssetsVisible(true);
  }, [hasBuildDock]);

  useEffect(() => {
    if (!requestedProject || inviteParam !== "1") return;
    const inviteKey = `${requestedProject}:1`;
    if (inviteAcceptAttemptedRef.current === inviteKey) return;
    inviteAcceptAttemptedRef.current = inviteKey;

    let cancelled = false;
    acceptInvitation(requestedProject)
      .catch(() => {
        // Already-accepted invites return no pending invite; the project load below
        // still decides whether the signed-in user has access.
      })
      .finally(() => {
        if (cancelled) return;
        const next = new URLSearchParams(searchParams);
        next.delete("invite");
        setSearchParams(next, { replace: true });
      });

    return () => {
      cancelled = true;
    };
  }, [inviteParam, requestedProject, searchParams, setSearchParams]);
  const animationTimersRef = useRef<number[]>([]);
  const particleQueueRef = useRef<V3Particle[]>([]);
  const particleRafRef = useRef<number | null>(null);
  const particleCleanupTimersRef = useRef<number[]>([]);
  const deckAnimationRef = useRef<number | null>(null);
  const lastMeltOverlayRef = useRef<V3GridCellOverlayItem[]>([]);
  const derived = useMemo(
    () => deriveThreadSelection(data.rows, data.assets, data.clusters, selection),
    [data.assets, data.clusters, data.rows, selection],
  );
  const provenanceSelection = useMemo(
    () => deriveProvenanceSelection(data.provenance, data.assets, selection),
    [data.assets, data.provenance, selection],
  );
  const pivotViews = useMemo(
    () => derivePivotCandidates(data.rows, data.columns, data.provenance, provenanceSelection.litNodeIds),
    [data.columns, data.provenance, data.rows, provenanceSelection.litNodeIds],
  );
  const [activePivotId, setActivePivotId] = useState<string | null>(null);
  const activePivot = useMemo(
    () => pivotViews.find((pivot) => pivot.id === activePivotId) ?? pivotViews.find(pivotIsAvailable) ?? pivotViews[0] ?? null,
    [activePivotId, pivotViews],
  );
  const flatAvailable = pivotIsAvailable(activePivot);
  const tableAvailable = data.rows.length > 0;
  const filterActive = filteredRowIds !== null;
  const filteredRowCount = filteredRowIds?.size ?? data.rows.length;
  const filteredDagNodeIds = useMemo(
    () => dagNodeIdsForFilteredRows(data, filteredRowIds),
    [data, filteredRowIds],
  );
  const filteredDagClusterIds = useMemo(() => {
    const next = new Set<string>();
    for (const nodeId of filteredDagNodeIds ?? []) {
      const node = data.provenance.nodesById.get(nodeId);
      if (node?.cluster) next.add(node.cluster);
    }
    return next;
  }, [data.provenance.nodesById, filteredDagNodeIds]);
  const dagSurfaceActive = surfaceMode === "dag";
  const projectionTracing = projectionDirection !== null;
  const projectionRestoring = projectionDirection === "dag-to-table";
  const visibleLitNodeIds = useMemo(
    () => new Set([...provenanceSelection.litNodeIds, ...meltTargetNodeIds]),
    [meltTargetNodeIds, provenanceSelection.litNodeIds],
  );
  const topologyClusters = useMemo(
    () => {
      const next = new Set([...topologySliceClusterIds(data, derived, selection), ...provenanceSelection.litClusterIds]);
      for (const nodeId of meltTargetNodeIds) {
        const node = data.provenance.nodesById.get(nodeId);
        if (node?.cluster) next.add(node.cluster);
      }
      return next;
    },
    [data, derived, meltTargetNodeIds, provenanceSelection.litClusterIds, selection],
  );
  const litRowIds = useMemo(() => new Set([...derived.litRowIds, ...provenanceSelection.litRowIds]), [derived.litRowIds, provenanceSelection.litRowIds]);
  const litAssetIds = useMemo(() => new Set([...derived.litAssetIds, ...provenanceSelection.litAssetIds]), [derived.litAssetIds, provenanceSelection.litAssetIds]);
  const incompatible = hasIncompatible(data, selection);
  const sliceActive = selection.primary.kind !== "none";
  const dagSliceActive = sliceActive || dagSurfaceActive || projectionTracing || meltTargetNodeIds.size > 0 || filterActive;
  const topologyExpanded = dagMode === "focus" || (dagMode === "ambient" && topologyFocus);
  const tableUnavailableReason = "Table view needs at least one loaded row.";
  const className = [
    "thread-v3",
    `variant-${variantId}`,
    `surface-${surfaceMode}`,
    projectionDirection ? `projection-${projectionDirection}` : "",
    `dag-${dagMode}`,
    topologyExpanded ? "topology-focus" : "",
    sliceActive ? "slice-focus" : "",
    combing ? "is-combing" : "",
    dagSurfaceActive ? "projection-melted" : "",
    projectionRestoring ? "is-freezing" : "",
    cellOverlay.length ? "has-cell-overlay" : "",
    filterActive ? "grid-filter-active" : "",
  ].filter(Boolean).join(" ");

  useEffect(() => {
    const nextProjection = projectionModeFromSearch(searchParams);
    setProjectionState((current) => current === nextProjection ? current : nextProjection);
  }, [searchParams]);

  const setProjection = useCallback((nextProjection: ProjectionMode) => {
    setProjectionState(nextProjection);
    const next = new URLSearchParams(searchParams);
    if (nextProjection === "table") next.delete("projection");
    else next.set("projection", nextProjection);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const openProject = useCallback((project: Project) => {
    const next = new URLSearchParams(searchParams);
    next.set("project", project.id);
    const branch = projectBranch(project);
    if (branch) next.set("branch", branch);
    else next.delete("branch");
    next.delete("fixture");
    setSelection({ primary: { kind: "none" } });
    setFilteredRowIds(null);
    setActivePivotId(null);
    setProjectMenuOpen(false);
    navigate(`/v3?${next.toString()}`);
  }, [navigate, searchParams]);

  const createAndOpenProject = useCallback(async () => {
    const project = await projectDirectory.create(projectDraftName);
    if (!project) return;
    setProjectDraftName("Untitled Thread project");
    openProject(project);
  }, [openProject, projectDirectory, projectDraftName]);

  useEffect(() => {
    setFilteredRowIds(null);
  }, [data.rows]);

  const updateFilteredRowIds = useCallback((next: Set<string> | null) => {
    setFilteredRowIds((current) => rowIdSetsEqual(current, next) ? current : next);
  }, []);

  const clearAnimationTimers = useCallback((resetParticles = true) => {
    for (const timer of animationTimersRef.current) {
      window.clearTimeout(timer);
    }
    animationTimersRef.current = [];
    for (const timer of particleCleanupTimersRef.current) {
      window.clearTimeout(timer);
    }
    particleCleanupTimersRef.current = [];
    particleQueueRef.current = [];
    if (particleRafRef.current != null) {
      window.cancelAnimationFrame(particleRafRef.current);
      particleRafRef.current = null;
    }
    if (deckAnimationRef.current != null) {
      window.cancelAnimationFrame(deckAnimationRef.current);
      deckAnimationRef.current = null;
    }
    if (resetParticles) setParticles([]);
  }, []);

  const scheduleAnimation = useCallback((callback: () => void, delay: number) => {
    const timer = window.setTimeout(() => {
      animationTimersRef.current = animationTimersRef.current.filter((active) => active !== timer);
      callback();
    }, delay);
    animationTimersRef.current.push(timer);
    return timer;
  }, []);

  useEffect(() => () => clearAnimationTimers(false), [clearAnimationTimers]);

  useEffect(() => {
    if (load.status === "loading") return;
    if (!tableAvailable && surfaceMode === "table") {
      setSurfaceMode("dag");
      setProjectionProgress(1);
      setDagMode("focus");
    }
  }, [load.status, surfaceMode, tableAvailable]);

  const updateDagMode = useCallback((next: V3DagMode) => {
    setDagMode(next);
    setTopologyFocus(next === "focus");
    if (next === "hidden") {
      clearAnimationTimers();
      setSurfaceMode("table");
      setProjectionDirection(null);
      setProjectionProgress(0);
      setCombing(false);
      setCellOverlay([]);
      lastMeltOverlayRef.current = [];
      setMeltTargetNodeIds(new Set());
    }
  }, [clearAnimationTimers]);

  const moveDagNode = useCallback((nodeId: string, position: V3DagPinnedPosition) => {
    updateDagLayout((layout) => ({
      ...layout,
      nodePins: {
        ...layout.nodePins,
        [nodeId]: normalizePinnedPosition(position),
      },
    }));
  }, [updateDagLayout]);

  const dropDagNode = useCallback((nodeId: string, position: V3DagPinnedPosition) => {
    updateDagLayout((layout) => {
      const nextPosition = normalizePinnedPosition(position);
      const next: V3DagLayoutState = {
        ...layout,
        nodePins: {
          ...layout.nodePins,
          [nodeId]: nextPosition,
        },
      };
      const membershipCluster = nearestMembershipCluster(data, next, nodeId, nextPosition);
      if (!membershipCluster) return next;
      return {
        ...next,
        memberships: {
          ...next.memberships,
          [nodeId]: [...new Set([...(next.memberships[nodeId] ?? []), membershipCluster])].slice(0, 5),
        },
      };
    });
  }, [data, updateDagLayout]);

  const moveDagCluster = useCallback((clusterId: string, position: V3DagPinnedPosition) => {
    updateDagLayout((layout) => ({
      ...layout,
      clusterPins: {
        ...layout.clusterPins,
        [clusterId]: normalizePinnedPosition(position),
      },
    }));
  }, [updateDagLayout]);

  const emitParticle = useCallback((from: { x: number; y: number }, to: { x: number; y: number }, nodeId: string, kind: string, index = 0, label?: string) => {
    const id = `${nodeId}-${Date.now()}-${index}-${Math.random().toString(36).slice(2)}`;
    const delayMs = Math.min(index * particleDelayStep(kind), kind === "melt" ? 520 : 360);
    const durationMs = particleDuration(kind);
    particleQueueRef.current.push({ id, nodeId, from, to, kind, delayMs, durationMs, label });
    if (particleRafRef.current != null) return;
    particleRafRef.current = window.requestAnimationFrame(() => {
      particleRafRef.current = null;
      const batch = particleQueueRef.current.splice(0);
      if (!batch.length) return;
      const ids = new Set(batch.map((particle) => particle.id));
      setParticles((current) => [...current, ...batch].slice(-MAX_ACTIVE_PARTICLES));
      const lifetime = Math.max(...batch.map((particle) => particle.delayMs + particle.durationMs), 0) + 160;
      const cleanupTimer = window.setTimeout(() => {
        particleCleanupTimersRef.current = particleCleanupTimersRef.current.filter((active) => active !== cleanupTimer);
        setParticles((current) => current.filter((particle) => !ids.has(particle.id)));
      }, lifetime);
      particleCleanupTimersRef.current.push(cleanupTimer);
    });
  }, []);

  const animateDeckViewState = useCallback((next: V3DeckViewState, duration = 320) => {
    if (deckAnimationRef.current != null) {
      window.cancelAnimationFrame(deckAnimationRef.current);
      deckAnimationRef.current = null;
    }
    const start = deckViewState;
    const startedAt = performance.now();
    const step = (now: number) => {
      const progress = Math.min(1, Math.max(0, (now - startedAt) / duration));
      setDeckViewState(interpolateDeckViewState(start, next, easeOutCubic(progress)));
      if (progress < 1) {
        deckAnimationRef.current = window.requestAnimationFrame(step);
      } else {
        deckAnimationRef.current = null;
        setDeckViewState(next);
      }
    };
    deckAnimationRef.current = window.requestAnimationFrame(step);
  }, [deckViewState]);

  const restoreProjection = useCallback(() => {
    if (!tableAvailable) return;
    clearAnimationTimers();
    const root = document.querySelector<HTMLElement>("[data-testid='thread-v3-workbench']");
    setProjectionDirection("dag-to-table");
    setProjectionProgress(0.5);
    setCombing(true);
    if (root) {
      const rootRect = root.getBoundingClientRect();
      const overlayCells: V3GridCellOverlayItem[] = [];
      const revealIds = new Set(meltTargetNodeIds);

      const visibleCellSnapshots = lastMeltOverlayRef.current.length > 0
        ? lastMeltOverlayRef.current
        : Array.from(root.querySelectorAll<HTMLElement>(".v3-cell-handle[data-node-id]"))
          .filter((cell) => {
            if (!cell.dataset.nodeId) return false;
            const rect = cell.getBoundingClientRect();
            return rect.bottom >= rootRect.top && rect.top <= rootRect.bottom && rect.right >= rootRect.left && rect.left <= rootRect.right;
          })
          .slice(0, MELT_VISIBLE_CELL_LIMIT)
          .map((cell, index): V3GridCellOverlayItem | null => {
            const nodeId = cell.dataset.nodeId ?? "";
            const sourceNodeId = sourceNodeIdForCellHandle(data, nodeId);
            const to = graphNodeScreenPosition(sourceNodeId, data, root, deckViewState, dagLayout);
            if (!nodeId || !to) return null;
            const agCell = cell.closest<HTMLElement>(".ag-cell") ?? cell;
            return {
              id: `${nodeId}-freeze-source-${index}`,
              nodeId,
              rowId: cell.dataset.rowId ?? "",
              rowNodeId: cell.dataset.rowNodeId ?? "",
              colId: cell.dataset.colId ?? "",
              text: cell.textContent?.trim() ?? "",
              direction: "to-dag",
              from: elementRectInRoot(agCell, root),
              to,
            };
          })
          .filter((cell): cell is V3GridCellOverlayItem => Boolean(cell));

      visibleCellSnapshots.forEach((cellSnapshot, index) => {
          const nodeId = cellSnapshot.nodeId;
          const sourceNodeId = sourceNodeIdForCellHandle(data, nodeId);
          const from = graphNodeScreenPosition(sourceNodeId, data, root, deckViewState, dagLayout) ?? cellSnapshot.to;
          if (!nodeId) return;
          revealIds.add(sourceNodeId);
          overlayCells.push({
            id: `${nodeId}-freeze-${index}-${Date.now()}`,
            nodeId,
            rowId: cellSnapshot.rowId,
            rowNodeId: cellSnapshot.rowNodeId,
            colId: cellSnapshot.colId,
            text: cellSnapshot.text,
            direction: "to-flat",
            from: cellSnapshot.from,
            to: from,
          });
        });

      setMeltTargetNodeIds(revealIds);
      setCellOverlay(overlayCells);
    }
    scheduleAnimation(() => {
      setSurfaceMode("table");
      setProjectionProgress(0);
    }, Math.round(CELL_OVERLAY_FREEZE_MS * 0.62));
    scheduleAnimation(() => {
      setMeltTargetNodeIds(new Set());
      setCellOverlay([]);
      lastMeltOverlayRef.current = [];
      setCombing(false);
      setProjectionDirection(null);
      setDagMode("ambient");
      setTopologyFocus(false);
      updateDock("dag-minimap", compactDagDockState());
    }, CELL_OVERLAY_FREEZE_MS + 360);
  }, [clearAnimationTimers, dagLayout, data, deckViewState, meltTargetNodeIds, scheduleAnimation, tableAvailable, updateDock]);

  const pulseToNode = useCallback((source: HTMLElement | null, nodeId: string, kind = "cell") => {
    if (!nodeId) return;
    const root = document.querySelector<HTMLElement>("[data-testid='thread-v3-workbench']");
    const primaryTargetId = kind === "cell" ? sourceNodeIdForCellHandle(data, nodeId) : nodeId;
    const from = elementCenter(source, root) ?? graphNodeScreenPosition(primaryTargetId, data, root, deckViewState, dagLayout);
    if (!root || !from) return;
    if (kind === "cell") {
      const upstream = [...walkProvenanceUpstream(data.provenance, [primaryTargetId], 4, 28)];
      const downstream = [...walkProvenanceDownstream(data.provenance, [primaryTargetId], 4, 28)];
      const targetIds = focusNodeIdsForSelection(data, [primaryTargetId, ...upstream, ...downstream]);
      if (!targetIds.length) return;
      const fitViewState = deckViewStateForNodes(targetIds, data, root, deckViewState, dagLayout, { padding: 1.44, minZoom: -3, maxZoom: 2.4 });
      setMeltTargetNodeIds(new Set(targetIds));
      animateDeckViewState(fitViewState, 260);
      const sourceText = source?.textContent?.trim() ?? "";
      scheduleAnimation(() => {
        const latestRoot = document.querySelector<HTMLElement>("[data-testid='thread-v3-workbench']");
        targetIds.forEach((targetId, index) => {
          const target = graphNodeScreenPosition(targetId, data, latestRoot, fitViewState, dagLayout);
          if (!target) return;
          emitParticle(from, target, targetId, index === 0 ? "cell" : "melt", index, index < 12 ? sourceText : undefined);
        });
      }, 210);
      return;
    }
    const targetIds = focusNodeIdsForSelection(data, [primaryTargetId]);
    const fitViewState = deckViewStateForNodes(targetIds, data, root, deckViewState, dagLayout, { padding: 1.55, minZoom: -3, maxZoom: 2.4 });
    setMeltTargetNodeIds(new Set(targetIds));
    animateDeckViewState(fitViewState, 260);
    scheduleAnimation(() => {
      const latestRoot = document.querySelector<HTMLElement>("[data-testid='thread-v3-workbench']");
      const to = graphNodeScreenPosition(primaryTargetId, data, latestRoot, fitViewState, dagLayout);
      if (to) emitParticle(from, to, nodeId, kind, 0);
    }, 210);
  }, [animateDeckViewState, dagLayout, data, deckViewState, emitParticle, scheduleAnimation]);

  const traceProjectionToDag = useCallback(() => {
    if (projectionTracing) return;
    if (surfaceMode === "dag") {
      restoreProjection();
      return;
    }
    clearAnimationTimers();
    const root = document.querySelector<HTMLElement>("[data-testid='thread-v3-workbench']");
    if (!root) return;
    const rootRect = root.getBoundingClientRect();
    const visibleCellSnapshots = Array.from(root.querySelectorAll<HTMLElement>(".v3-cell-handle[data-node-id]"))
      .flatMap((cell) => {
        const nodeId = cell.dataset.nodeId ?? "";
        if (!nodeId) return [];
        const rect = cell.getBoundingClientRect();
        const visible = rect.bottom >= rootRect.top && rect.top <= rootRect.bottom && rect.right >= rootRect.left && rect.left <= rootRect.right;
        if (!visible) return [];
        const agCell = cell.closest<HTMLElement>(".ag-cell") ?? cell;
        const agRect = agCell.getBoundingClientRect();
        return [{
          nodeId,
          from: {
            x: rect.left + rect.width / 2 - rootRect.left,
            y: rect.top + rect.height / 2 - rootRect.top,
          },
          fromRect: {
            x: agRect.left - rootRect.left,
            y: agRect.top - rootRect.top,
            width: agRect.width,
            height: agRect.height,
          },
          rowId: cell.dataset.rowId ?? "",
          rowNodeId: cell.dataset.rowNodeId ?? "",
          colId: cell.dataset.colId ?? "",
          text: cell.textContent?.trim() ?? "",
        }];
      })
      .slice(0, MELT_VISIBLE_CELL_LIMIT);
    const fragmentsPerCell = clampNumber(Math.floor(MELT_FRAGMENT_BUDGET / Math.max(visibleCellSnapshots.length, 1)), 1, 6);
    const flights = visibleCellSnapshots
      .flatMap((cell, index) => {
        const targetNodeIds = sourceAtomIdsForCellHandle(data, cell.nodeId, fragmentsPerCell);
        if (!targetNodeIds.length) return [];
        return [{
          ...cell,
          targetNodeIds,
          index,
          label: index < 24 ? cell.text : undefined,
        }];
      });
    const revealIds = new Set<string>();
    for (const flight of flights) {
      for (const targetNodeId of flight.targetNodeIds) revealIds.add(targetNodeId);
    }
    const nextDeckViewState = deckViewStateForNodes(revealIds, data, root, deckViewState, dagLayout);
    setMeltTargetNodeIds(revealIds);
    animateDeckViewState(nextDeckViewState, 360);
    updateDagMode("focus");
    setProjectionDirection("table-to-dag");
    setProjectionProgress(0.5);
    setCombing(true);
    const overlayCells: V3GridCellOverlayItem[] = [];
    for (const flight of flights) {
      const primaryTarget = flight.targetNodeIds[0];
      if (!primaryTarget) continue;
      const to = graphNodeScreenPosition(primaryTarget, data, root, nextDeckViewState, dagLayout);
      if (!to) continue;
      overlayCells.push({
        id: `${flight.nodeId}-${flight.index}-${Date.now()}`,
        nodeId: flight.nodeId,
        rowId: flight.rowId,
        rowNodeId: flight.rowNodeId,
        colId: flight.colId,
        text: flight.text,
        direction: "to-dag",
        from: flight.fromRect,
        to,
      });
    }
    lastMeltOverlayRef.current = overlayCells;
    setCellOverlay(overlayCells);
    scheduleAnimation(() => {
      setCellOverlay([]);
      setSurfaceMode("dag");
      setProjectionDirection(null);
      setProjectionProgress(1);
    }, CELL_OVERLAY_MELT_MS + 80);
    scheduleAnimation(() => setCombing(false), CELL_OVERLAY_MELT_MS + 360);
  }, [animateDeckViewState, clearAnimationTimers, dagLayout, data, deckViewState, projectionTracing, restoreProjection, scheduleAnimation, surfaceMode, updateDagMode]);

  const handleWorkbenchWheel = useCallback((event: ReactWheelEvent<HTMLElement>) => {
    if (surfaceMode === "table" && dagMode !== "focus") return;
    const deckLayer =
      event.currentTarget.querySelector<HTMLElement>('[data-testid="v3-floating-dock-dag-minimap"] .v3-deck-dag-ready') ??
      event.currentTarget.querySelector<HTMLElement>(".v3-deck-dag-ready");
    if (!deckLayer) return;
    const rect = deckLayer.getBoundingClientRect();
    const insideDeck =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;
    if (!insideDeck) return;
    event.preventDefault();
    event.stopPropagation();
    setDeckViewState(wheelZoomViewState(event, deckLayer, deckViewState));
  }, [dagMode, deckViewState, surfaceMode]);

  const openTableView = useCallback(() => {
    if (!tableAvailable) return;
    showDock("dag-minimap");
    setDagWidgetVisible(true);
    clearAnimationTimers();
    setSurfaceMode("table");
    setProjectionDirection(null);
    setProjectionProgress(0);
    setCombing(false);
    setCellOverlay([]);
    setMeltTargetNodeIds(new Set());
    updateDock("dag-minimap", compactDagDockState());
    updateDagMode("ambient");
  }, [clearAnimationTimers, showDock, tableAvailable, updateDagMode, updateDock]);

  const openDagView = useCallback(() => {
    setDagWidgetVisible(true);
    updateDock("dag-minimap", expandedDagDockState());
    updateDock("flat-minimap", flatMinimapDockState());
    showDock("dag-minimap");
    showDock("flat-minimap");
    clearAnimationTimers();
    setSurfaceMode("dag");
    setProjectionDirection(null);
    setProjectionProgress(1);
    setCombing(false);
    setCellOverlay([]);
    updateDagMode("focus");
  }, [clearAnimationTimers, showDock, updateDagMode, updateDock]);

  const openSplitView = useCallback(() => {
    if (!tableAvailable) return;
    setDagWidgetVisible(true);
    updateDock("dag-minimap", expandedDagDockState());
    showDock("dag-minimap");
    if (flatAvailable) showDock("flat-minimap");
    clearAnimationTimers();
    setSurfaceMode("split");
    setProjectionDirection(null);
    setProjectionProgress(0);
    setCombing(false);
    setCellOverlay([]);
    updateDagMode("focus");
  }, [clearAnimationTimers, flatAvailable, showDock, tableAvailable, updateDagMode, updateDock]);

  const applyLayoutPreset = useCallback((preset: V3LayoutPresetId) => {
    if (preset === "tsangpo-build") {
      openTableView();
      setAssetsVisible(false);
      hideDock("flat-minimap");
      if (hasBuildDock) {
        showDock("tsangpo-build");
        updateDock("tsangpo-build", tsangpoBuildDockState());
      }
      return;
    }

    if (preset === "table-focus") {
      openTableView();
      setDagWidgetVisible(false);
      setAssetsVisible(false);
      hideDock("dag-minimap");
      hideDock("flat-minimap");
      if (hasBuildDock) {
        showDock("tsangpo-build");
        updateDock("tsangpo-build", { ...tsangpoBuildDockState(), minimized: true });
      }
      return;
    }

    if (preset === "dag-review") {
      openDagView();
      setAssetsVisible(true);
      if (hasBuildDock) {
        showDock("tsangpo-build");
        updateDock("tsangpo-build", { ...tsangpoBuildDockState(), minimized: true });
      }
      return;
    }

    openSplitView();
    setAssetsVisible(true);
    if (hasBuildDock) {
      showDock("tsangpo-build");
      updateDock("tsangpo-build", tsangpoBuildDockState());
    }
  }, [hasBuildDock, hideDock, openDagView, openSplitView, openTableView, showDock, updateDock]);

  const traceProjection = useCallback(() => {
    if (projectionTracing || !tableAvailable) return;
    if (surfaceMode === "dag") {
      traceProjectionToDag();
      return;
    }
    setDagWidgetVisible(true);
    showDock("dag-minimap");
    updateDock("dag-minimap", expandedDagDockState());
    updateDock("flat-minimap", flatMinimapDockState());
    showDock("flat-minimap");
    if (surfaceMode === "table") {
      scheduleAnimation(() => {
        window.requestAnimationFrame(() => traceProjectionToDag());
      }, 390);
      return;
    }
    window.requestAnimationFrame(() => traceProjectionToDag());
  }, [projectionTracing, scheduleAnimation, showDock, surfaceMode, tableAvailable, traceProjectionToDag, updateDock]);

  function setVariant(next: V3VariantId) {
    const params = new URLSearchParams(searchParams);
    params.set("variant", next);
    setSearchParams(params, { replace: true });
  }

  return (
    <main className={className} data-testid="thread-v3-workbench" data-variant={variantId} data-projection-progress={projectionProgress.toFixed(2)} onWheelCapture={handleWorkbenchWheel}>
      <V3GridCellOverlay cells={cellOverlay} />
      <V3ParticleOverlay particles={particles} />
      {dagMode === "ambient" && surfaceMode === "table" && <V3TetherOverlay litAssetIds={litAssetIds} assets={data.assets} />}
      <header className="v3-chrome" data-testid="thread-v3-chrome">
        <div className="v3-chrome-left">
          <ThreadLogo height={22} />
          <span className="v3-product">V3</span>
          <button
            type="button"
            className="v3-project-home"
            onClick={() => navigate("/projects")}
          >
            Projects
          </button>
          <V3ProjectSwitcher
            currentProject={data.project}
            currentBranch={data.branch}
            open={projectMenuOpen}
            directory={projectDirectory}
            draftName={projectDraftName}
            onDraftNameChange={setProjectDraftName}
            onToggle={() => setProjectMenuOpen((value) => !value)}
            onSelectProject={openProject}
            onCreateProject={createAndOpenProject}
          />
          {membersProjectId && (
            <Dialog.Root open={membersOpen} onOpenChange={setMembersOpen}>
              <Dialog.Trigger>
                <button
                  type="button"
                  className="v3-members-trigger"
                  disabled={!membersReady}
                  title={membersReady ? "Manage project members" : "Preparing a real Thread project for Tsangpo invites"}
                >
                  Members / Invite
                </button>
              </Dialog.Trigger>
              <Dialog.Content data-testid="v3-members-dialog" aria-describedby={undefined} style={{ maxWidth: 680 }}>
                <Dialog.Title>Project Members</Dialog.Title>
                <MembersPanel projectId={membersProjectId} />
              </Dialog.Content>
            </Dialog.Root>
          )}
        </div>
        <div className="v3-chrome-meta">
          <span className="v3-chrome-note">The table is the DAG</span>
          <span className="v3-active-variant">{variant.label}</span>
          <span className={`v3-load ${load.status}`}>{load.status}</span>
          <V3ScaleMetrics
            data={data}
            activePivot={activePivot}
            filteredRowCount={filteredRowCount}
            filterActive={filterActive}
          />
          <label className="v3-pivot-picker">
            <span>Pivot</span>
            <select
              data-testid="v3-pivot-select"
              value={activePivot?.id ?? ""}
              onChange={(event) => setActivePivotId(event.target.value)}
            >
              {pivotViews.map((pivot) => (
                <option key={pivot.id} value={pivot.id}>{pivot.label}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="v3-chrome-actions">
          <V3LayoutPresetPicker hasBuildDock={hasBuildDock} onApply={applyLayoutPreset} />
          <div className="v3-dag-controls" aria-label="Surface views">
            <button
              type="button"
              className={surfaceMode === "table" ? "active" : ""}
              disabled={!tableAvailable}
              title={tableAvailable ? "Open the combed table projection" : tableUnavailableReason}
              onClick={openTableView}
            >
              Table
            </button>
            <button
              type="button"
              className={surfaceMode === "dag" ? "active" : ""}
              onClick={openDagView}
            >
              DAG
            </button>
            <button
              type="button"
              className={surfaceMode === "split" ? "active" : ""}
              disabled={!tableAvailable}
              title={tableAvailable ? "Show the table and DAG together" : tableUnavailableReason}
              onClick={openSplitView}
            >
              Split
            </button>
            <button
              type="button"
              className={projectionTracing ? "active" : ""}
              disabled={!tableAvailable || projectionTracing}
              title={tableAvailable ? "Animate between the DAG and the flat projection" : tableUnavailableReason}
              onClick={traceProjection}
            >
              Trace Projection
            </button>
            <button
              type="button"
              className={dagWidgetVisible && !docks["dag-minimap"].hidden ? "active" : ""}
              data-testid="v3-dag-widget-toggle"
              onClick={() => {
                if (dagWidgetVisible && !docks["dag-minimap"].hidden) {
                  hideDock("dag-minimap");
                  setDagWidgetVisible(false);
                } else {
                  showDock("dag-minimap");
                  setDagWidgetVisible(true);
                }
              }}
            >
              DAG minimap
            </button>
            <button
              type="button"
              className={assetsVisible ? "active" : ""}
              data-testid="v3-assets-toggle"
              onClick={() => setAssetsVisible((visible) => !visible)}
            >
              Artifacts
            </button>
          </div>
        </div>
      </header>
      {searchParams.has("variantSwitcher") && <V3VariantSwitcher active={variantId} onChange={setVariant} />}
      <V3VariantLayout
        variantId={variantId}
        data={data}
        selection={selection}
        projectionMode={projection}
        litClusterIds={topologyClusters}
        litNodeIds={visibleLitNodeIds}
        selectedNodeId={provenanceSelection.selectedNodeId}
        filteredNodeIds={filteredDagNodeIds}
        filteredClusterIds={filteredDagClusterIds}
        filterActive={filterActive}
        filteredRowIds={filteredRowIds}
        filteredRowCount={filteredRowCount}
        litRowIds={litRowIds}
        litAssetIds={litAssetIds}
        sliceActive={dagSliceActive}
        variant={variant}
        surfaceMode={surfaceMode}
        projectionDirection={projectionDirection}
        dagWidgetVisible={dagWidgetVisible}
        assetsVisible={assetsVisible}
        docks={docks}
        activePivot={activePivot}
        dagLayout={dagLayout}
        deckViewState={deckViewState}
        onFocusChange={(focused) => {
          if (dagMode === "ambient") setTopologyFocus(focused);
        }}
        onProjectionChange={setProjection}
        onSelectionChange={setSelection}
        onPulseToNode={pulseToNode}
        onFilterRowsChange={updateFilteredRowIds}
        onSelectDagNode={(nodeId) => setSelection({ primary: { kind: "dag-node", nodeId } })}
        onComb={traceProjection}
        onToggleDagWidget={() => {
          setDagWidgetVisible((visible) => !visible);
          if (dagWidgetVisible) hideDock("dag-minimap");
          else showDock("dag-minimap");
        }}
        onToggleAssets={() => setAssetsVisible((visible) => !visible)}
        onDockChange={updateDock}
        onDockHide={hideDock}
        onDockReset={resetDock}
        onOpenFlat={openTableView}
        onDeckViewStateChange={setDeckViewState}
        onDagLayoutChange={updateDagLayout}
        onDagLayoutReset={resetDagLayout}
        onMoveDagNode={moveDagNode}
        onDropDagNode={dropDagNode}
        onMoveDagCluster={moveDagCluster}
        onDeepInspect={() => setDeepOpen(true)}
        codeSourceProjectId={membersProjectId}
      />
      {incompatible && (
        <aside className="v3-not-comparable" data-testid="v3-incompatible-panel">
          <strong>Compatibility bridge missing</strong>
          <span>Same pivot view, different model lineage. Thread has not been given a trusted bridge for this output, so comparisons and grouped aggregates are marked unvalidated.</span>
        </aside>
      )}
      {data.errors.length > 0 && (
        <aside className="v3-errors">
          {data.errors.slice(0, 2).map((error) => <span key={error}>{error}</span>)}
        </aside>
      )}
      {deepOpen && <V3DeepGraphModal data={data} onClose={() => setDeepOpen(false)} />}
      <V3CompatibilityLegend incompatible={incompatible} />
    </main>
  );
}

export default ThreadV3WorkbenchPage;
